import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  FileText, Printer, Loader2, Calendar, School, Users,
  Filter, ChevronDown, ChevronUp, RefreshCw, Settings,
  Search, GraduationCap, FileSpreadsheet, FileDown,
  Eye, X, Save, Building2, Lock
} from "lucide-react";
import { apiService } from "@/services/api";
import { useAuthStore } from "@/store/authStore";
import { toast } from "sonner";
import { useLang } from "@/hooks/useLang";

// ==================== HELPERS ====================
const extractData = (res: any): any[] => {
  if (Array.isArray(res)) return res;
  if (res && typeof res === 'object') {
    if (Array.isArray(res.data)) return res.data;
    if (Array.isArray(res.items)) return res.items;
    if (Array.isArray(res.results)) return res.results;
  }
  return [];
};

const AUTH_STORAGE_KEY = 'auth-storage';
const SELECTED_YEAR_ID_KEY = 'lybibal-selected-year-id';
const SELECTED_YEAR_LABEL_KEY = 'lybibal-selected-year-label';
const SELECTED_YEAR_EVENT = 'lybibal-year-change';

const getUserSchoolId = (): number | null => {
  try {
    const authRaw = localStorage.getItem(AUTH_STORAGE_KEY);
    if (authRaw) {
      const parsed = JSON.parse(authRaw);
      const state = parsed?.state || parsed;
      if (state?.user?.school_id) return Number(state.user.school_id);
    }
  } catch (e) { console.warn('Failed to get school_id:', e); }
  return null;
};

// Helper: charge avec school_id, fallback sans school_id si admin et résultat vide
const loadWithFallback = async (
  url: string,
  params: Record<string, any>,
  isPrivileged: boolean
): Promise<any[]> => {
  try {
    const res = await apiService.get(url, { params }).catch(() => ({ data: [] }));
    let data = extractData(res);
    if (data.length === 0 && params.school_id && isPrivileged) {
      const { school_id, ...paramsNoSchool } = params;
      const res2 = await apiService.get(url, { params: paramsNoSchool }).catch(() => ({ data: [] }));
      data = extractData(res2);
      if (data.length > 0) {
        console.warn(`[${url}] Fallback without school_id returned ${data.length} items`);
      }
    }
    return data;
  } catch (e) {
    console.warn(`[${url}] Load error:`, e);
    return [];
  }
};

const getSelectedYearId = (): number | null => {
  try {
    const yearId = localStorage.getItem(SELECTED_YEAR_ID_KEY);
    if (yearId) return Number(yearId);
  } catch (e) { console.warn('Failed to get year_id:', e); }
  return null;
};

const getAppreciationColor = (score: number): string => {
  if (score >= 16) return 'text-emerald-700';
  if (score >= 14) return 'text-blue-700';
  if (score >= 12) return 'text-cyan-700';
  if (score >= 10) return 'text-gray-700';
  if (score >= 8) return 'text-orange-700';
  return 'text-red-700';
};

function resolvePhotoUrl(url?: string | null): string | null {
  if (!url) return null
  if (url.startsWith('http')) return url
  if (url.startsWith('/')) return `http://127.0.0.1:8001${url}`
  return `http://127.0.0.1:8001/${url}`
}

const getAppreciation = (score: number, isCompetency: boolean = false, lang: string = 'fr'): string => {
  if (isCompetency) {
    if (score >= 16) return lang === 'fr' ? 'Acquise' : 'Acquired';
    if (score >= 12) return lang === 'fr' ? "En cours d'acquisition" : 'In progress';
    return lang === 'fr' ? 'Non acquise' : 'Not acquired';
  }
  if (lang === 'fr') {
    if (score >= 18) return 'Excellent';
    if (score >= 16) return 'Très Bien';
    if (score >= 14) return 'Bien';
    if (score >= 12) return 'Assez Bien';
    if (score >= 10) return 'Passable';
    if (score >= 8) return 'Insuffisant';
    if (score >= 6) return 'Faible';
    return 'Très Faible';
  }
  if (score >= 18) return 'Excellent';
  if (score >= 16) return 'Very Good';
  if (score >= 14) return 'Good';
  if (score >= 12) return 'Fairly Good';
  if (score >= 10) return 'Pass';
  if (score >= 8) return 'Insufficient';
  if (score >= 6) return 'Weak';
  return 'Very Weak';
};

// ✅ NOUVEAU — Niveau d'acquisition (1 à 4) pour les classes à compétences
const getNiveauAcquisition = (score: number): number => {
  if (score >= 16) return 4;
  if (score >= 12) return 3;
  if (score >= 10) return 2;
  return 1;
};

// ✅ NOUVEAU — Cote (A+, A, B+, B, C+, C, D)
const getCote = (score: number): string => {
  if (score >= 18) return 'A+';
  if (score >= 16) return 'A';
  if (score >= 15) return 'B+';
  if (score >= 14) return 'B';
  if (score >= 12) return 'C+';
  if (score >= 10) return 'C';
  return 'D';
};

// ✅ Libellés dynamiques selon le type de bulletin + langue du bulletin
const getPeriodLabels = (bType: string, blang: 'fr' | 'en' = 'fr') => {
  if (blang === 'en') {
    return {
      mainTitle: bType === 'sequence' ? 'SEQUENTIAL' : bType === 'trimester' ? 'TERM' : 'ANNUAL',
      moyLabel: bType === 'sequence' ? 'SEQUENTIAL' : bType === 'trimester' ? 'TERM' : 'ANNUAL',
      rangLabel: bType === 'sequence' ? 'SEQUENTIAL' : bType === 'trimester' ? 'TERM' : 'ANNUAL',
    };
  }
  return {
    mainTitle: bType === 'sequence' ? 'SÉQUENTIELLE' : bType === 'trimester' ? 'TRIMESTRIELLE' : 'ANNUELLE',
    moyLabel: bType === 'sequence' ? 'SÉQUENTIELLE' : bType === 'trimester' ? 'TRIMESTRIELLE' : 'ANNUELLE',
    rangLabel: bType === 'sequence' ? 'SÉQUENTIEL' : bType === 'trimester' ? 'TRIMESTRIEL' : 'ANNUEL',
  };
};

// ✅ Langue du bulletin : anglais automatique si section anglophone
const getBulletinLang = (bulletin: BulletinData, uiLang: string): 'fr' | 'en' => {
  const section = `${bulletin?.student?.section_name || ''} ${bulletin?.class_info?.section || ''}`.toLowerCase();
  if (section.includes('angl') || section.includes('english')) return 'en';
  return uiLang === 'en' ? 'en' : 'fr';
};

// ✅ Libellés internes du bulletin selon la langue du bulletin
const getBulletinLabels = (blang: 'fr' | 'en') => {
  if (blang === 'en') {
    return {
      repLeft: false, // anglophone : anglais à gauche, français à droite
      titleSequence: 'REPORT CARD — SEQUENCE',
      titleTrimester: 'REPORT CARD — TERM',
      titleAnnual: 'REPORT CARD — ANNUAL',
      names: 'NAMES AND FIRST NAMES',
      dob: 'DATE OF BIRTH', pob: 'PLACE OF BIRTH',
      classLabel: 'CLASS', matricule: 'STUDENT ID',
      classTeacher: 'CLASS TEACHER', year: 'YEAR',
      gender: 'GENDER', male: 'Male', female: 'Female',
      situation: 'STATUS', repeater: 'Repeater', newStudent: 'New student',
      yes: 'YES', no: 'NO',
      subjects: 'SUBJECTS',
      note: 'GRADE', coef: 'COEF.', nxC: 'N x C', rank: 'RANK',
      acquisition: 'ACQUISITION OF COMPETENCIES',
      niveau: 'LEVEL', cote: 'GRADE', apprec: 'APPREC.',
      classScores: 'CLASS SCORES', min: 'MIN', moy: 'AVG', max: 'MAX',
      teacherSig: 'TEACHER AND\nSIGNATURE',
      total: 'TOTAL',
      prevTerms: 'PREVIOUS TERMS RECORD', avgShort: 'Avg:', rankShort: 'Rank:',
      workSeq: 'SEQUENTIAL WORK', workTri: 'TERM WORK', workAnn: 'ANNUAL WORK',
      condSeq: 'SEQUENTIAL CONDUCT', condTri: 'TERM CONDUCT', condAnn: 'ANNUAL CONDUCT',
      evalRecord: 'EVALUATION RECORD',
      totalPoints: 'TOTAL POINTS', totalCoef: 'TOTAL COEF.',
      noSubjects: 'NO. OF SUBJECTS', classAvg: 'CLASS AVG',
      absTotal: 'TOTAL ABS.', absUnjust: 'UNJUST. ABS.',
      detentions: 'DETENTIONS (Days)', detHours: 'DETENTIONS (Hrs)',
      exclusions: 'EXCLUSIONS (Days)', conductNote: 'CONDUCT NOTE / 20',
      council: 'CLASS COUNCIL',
      honorRoll: 'HONOR ROLL', encouragements: 'ENCOURAGEMENTS', congratulations: 'CONGRATULATIONS',
      blameWork: 'WORK BLAME', blameConduct: 'CONDUCT BLAME',
      termRecord: 'TERM RECORD',
      eleve: 'STUDENT',
      classe: 'CLASS',
      parentsSig: "Parents' signature",
      distinctions: 'DISTINCTIONS',
      sanctions: 'SANCTIONS',
      avertWork: 'WARNING (WORK)',
      avertConduct: 'WARNING (CONDUCT)',
      moyPremier: 'HIGHEST AVG.',
      moyDernier: 'LOWEST AVG.',
      evalAvg: (i: number) => `EVALUATION ${i} AVG.`,
      termAvg: (i: number) => `TERM ${i} AVG.`,
      obsTitle: 'CLASS COUNCIL OBSERVATIONS',
      efforts: 'Efforts are required in:',
      visa: "PRINCIPAL'S ENDORSEMENT",
      profTitu: 'Class Teacher',
      principal: "Principal's Endorsement",
    };
  }
  return {
    repLeft: true, // francophone : français à gauche, anglais à droite
    titleSequence: 'BULLETIN DE NOTES — SÉQUENCE',
    titleTrimester: 'BULLETIN DE NOTES — TRIMESTRE',
    titleAnnual: 'BULLETIN DE NOTES — ANNUEL',
    names: 'NOMS ET PRENOMS',
    dob: 'DATE NAISSANCE', pob: 'LIEU NAISSANCE',
    classLabel: 'CLASSE', matricule: 'MATRICULE',
    classTeacher: 'PROF. TITULAIRE', year: 'ANNEE',
    gender: 'SEXE', male: 'Masculin', female: 'Feminin',
    situation: 'SITUATION', repeater: 'Redoublant', newStudent: 'Nouveau',
    yes: 'OUI', no: 'NON',
    subjects: 'MATIERE',
    note: 'NOTE', coef: 'COEF.', nxC: 'N x C', rank: 'RANG',
    acquisition: 'ACQUISITION DES COMPÉTENCES',
    niveau: 'NIVEAU', cote: 'COTE', apprec: 'APPRÉC.',
    classScores: 'NOTE CLASSE', min: 'MIN', moy: 'MOY', max: 'MAX',
    teacherSig: 'ENSEIGNANT ET\nSIGNATURE',
    total: 'TOTAL',
    prevTerms: 'Rappel des trimestres précédents', avgShort: 'Moy:', rankShort: 'Rang:',
    workSeq: 'TRAVAIL SÉQUENTIEL', workTri: 'TRAVAIL TRIMESTRIEL', workAnn: 'TRAVAIL ANNUEL',
    condSeq: 'CONDUITE SÉQUENTIELLE', condTri: 'CONDUITE TRIMESTRIELLE', condAnn: 'CONDUITE ANNUELLE',
    evalRecord: 'RAPPEL EVALUATIONS',
    totalPoints: 'TOTAL POINTS', totalCoef: 'TOTAL COEF.',
    noSubjects: 'NBRE MATIERES', classAvg: 'MOY. CLASSE',
    absTotal: 'ABS. TOTALES', absUnjust: 'ABS. NON JUST.',
    detentions: 'CONSIGNES (Jrs)', detHours: 'CONSIGNES (Hrs)',
    exclusions: 'EXCLUSIONS (Jrs)', conductNote: 'NOTE CONDUITE / 20',
    council: 'CONSEIL DE CLASSE',
    honorRoll: "TABLEAU D'HONNEUR", encouragements: 'ENCOURAGEMENTS', congratulations: 'FELICITATIONS',
    blameWork: 'BLAME TRAVAIL', blameConduct: 'BLAME CONDUITE',
    termRecord: 'RAPPEL TRIMESTRES',
    eleve: 'ÉLÈVE',
    classe: 'CLASSE',
    parentsSig: 'Signature des parents',
    distinctions: 'DISTINCTIONS',
    sanctions: 'SANCTIONS',
    avertWork: 'AVERT. TRAVAIL',
    avertConduct: 'AVERT. CONDUITE',
    moyPremier: 'MOY. PREMIER',
    moyDernier: 'MOY. DERNIER',
    evalAvg: (i: number) => `MOYENNE EVAL. ${i}`,
    termAvg: (i: number) => `MOYENNE TRIM. ${i}`,
    obsTitle: 'OBSERVATIONS DU CONSEIL DE CLASSE',
    efforts: "Les efforts s'imposent en :",
    visa: "VISA DU CHEF D'ETABLISSEMENT",
    profTitu: 'Prof. Titulaire',
    principal: "Visa du Chef d'établissement",
  };
};

// Normalise un nom de rôle pour comparaison (super_admin -> superadmin)
const normalizeRole = (role: string | undefined): string => {
  return (role || '').toLowerCase().replace(/[_\s-]/g, '').trim();
};

// ==================== TYPES ====================
interface BulletinSubject {
  subject_id: number;
  subject_name: string;
  subject_code: string;
  coefficient: number;
  group?: string;
  group_name?: string;
  sequence_scores: number[];
  trimester_scores: number[];
  annual_average: number;
  display_note: number;
  points: number;
  rank: number;
  mention: string;
  appreciation: string;
  grades_count: number;
  class_stats: { min: number; max: number; avg: number };
  teacher_name?: string;
  competencies?: Array<{
    id: number;
    trimester: number;
    number: number;
    label_fr: string;
    label_en?: string;
    score: number;
    appreciation: string;
  }>;
}


interface BulletinData {
  student: {
    id: number;
    matricule: string;
    first_name: string;
    last_name: string;
    full_name: string;
    date_of_birth: string | null;
    place_of_birth: string | null;
    gender: string;
    class_name: string;
    cycle_name: string;
    level_name: string;
    section_name: string;
    enrollment_status: string;
    is_repeating?: boolean;
    photo_url?: string | null;
  };
  bulletin_type: string;
  sequence_id: number | null;
  evaluation_period_id: number | null;
  sequences: Array<{id: number; label: string; code: string; evaluation_period_id: number; weight: number}>;
  periods: Array<{id: number; code: string; label_fr: string; label_en: string}>;
  is_competence_approach: boolean;
  academic_year_id: string | number;
  subjects: BulletinSubject[];
  evaluations?: number[];
  trimesters?: number[];
  previous_trimesters?: Array<{
    period_id: number;
    period_code: string;
    period_label: string;
    average: number;
    rank: number;
  }>;
  weak_subjects?: Array<{
    subject_name: string;
    note: number;
  }>;
  annual_work?: {
    total_points: number;
    total_coef: number;
    moy_annuelle: number;
    rang_annuel: number;
    nbre_matieres: string;
    moy_classe: number;
    moy_premier?: number;
    moy_dernier?: number;
    class_size?: number;
  };
  summary: {
    total_points: number;
    total_coefficient: number;
    general_average: number;
    general_appreciation: string;
    rank: number;
    subjects_count: number;
  };
  conduct: {
    absence_hours: number;
    absence_unjustified: number;
    detention_days: number;
    exclusion_days: number;
    note: number;
  };
  council?: {
    tableau_honneur: string;
    encouragements: string;
    felicitations: string;
    blame_travail: string;
    blame_conduite: string;
    avertissement_travail?: string;
    avertissement_conduite?: string;
    observations: string;
  };
  teacher_name?: string;
  class_info?: {
    name: string;
    level: string;
    level_code: string;
    cycle: string;
    section: string;
  };
}

interface SchoolHeader {
  republic_fr: string;
  republic_en: string;
  motto_fr: string;
  motto_en: string;
  region: string;
  department: string;
  delegation: string;
  sub_division: string;
  school_name: string;
  school_name_en: string;
  address: string;
  po_box: string;
  logo_url: string;
}

export default function GradesReportCardsPage() {
  const { roleName } = useAuthStore();
  const { lang } = useLang();

  const [schoolId, setSchoolId] = useState<number | null>(getUserSchoolId);
  const [selectedAcademicYearId, setSelectedAcademicYearId] = useState<number | null>(getSelectedYearId);
  const [currentAcademicYear, setCurrentAcademicYear] = useState<{id: number; name: string} | null>(null);

  // Permissions — normalisé pour gérer super_admin, super admin, SuperAdmin, etc.
  const canGenerateBulletins = useMemo(() => {
    const allowed = ['superadmin', 'admin', 'proviseur', 'censeur'];
    const userRole = normalizeRole(roleName);
    console.log('[DEBUG] Role check:', { roleName, normalized: userRole, allowed });
    return allowed.includes(userRole);
  }, [roleName]);

  const t = {
    fr: {
      title: 'Bulletins de Notes',
      subtitle: 'Génération et impression des bulletins',
      configTitle: 'Configuration',
      section: 'Section',
      allSections: 'Toutes les sections',
      cycle: 'Cycle',
      allCycles: 'Tous les cycles',
      level: 'Niveau',
      allLevels: 'Tous les niveaux',
      class: 'Classe',
      allClasses: 'Toutes les classes',
      bulletinType: 'Type de bulletin',
      sequence: 'Séquence',
      trimester: 'Trimestre',
      annual: 'Annuelle',
      period: 'Période d\'évaluation',
      allPeriods: 'Toutes les périodes',
      allSequences: 'Toutes les séquences',
      studentFilters: 'Filtres élèves',
      enrolled: 'Inscrits',
      excluded: 'Exclus',
      resigned: 'Démissionnaires',
      decisions: 'Décisions du conseil',
      admitted: 'Admis',
      repeated: 'Redouble',
      demoted: 'Rétrogradé',
      expelled: 'Exclu',
      noDecision: 'Aucune décision',
      headerTitle: 'En-tête de l\'établissement',
      headerDesc: 'Personnalisez l\'en-tête qui apparaît sur chaque bulletin',
      schoolName: 'Nom de l\'établissement',
      schoolNameEn: 'Nom (EN)',
      address: 'Adresse',
      poBox: 'BP / P.O. Box',
      region: 'Région',
      department: 'Département',
      delegation: 'Délégation',
      subDivision: 'Arrondissement / Sub-Division',
      logo: 'Logo (URL)',
      saveHeader: 'Sauvegarder l\'en-tête',
      refresh: 'Actualiser',
      downloadPDF: 'Télécharger PDF',
      downloadExcel: 'Télécharger Excel',
      downloadExcelData: 'Excel Data',
      generateAll: 'Générer tous',
      generating: 'Génération...',
      sortBy: 'Trier par',
      sortMerit: 'Mérite (moyenne)',
      sortAlpha: 'Alphabétique',
      matricule: 'Matricule',
      student: 'Élève',
      average: 'Moyenne',
      rank: 'Rang',
      decision: 'Décision',
      actions: 'Actions',
      view: 'Voir',
      print: 'Imprimer',
      previewTitle: 'Aperçu du bulletin',
      closePreview: 'Fermer',
      noYearSelected: 'Aucune année scolaire sélectionnée',
      yearRequired: 'Veuillez sélectionner une année scolaire dans le header',
      noData: 'Sélectionnez une classe pour voir les bulletins',
      noStudents: 'Aucun élève trouvé',
      loading: 'Chargement...',
      error: 'Erreur',
      success: 'Succès',
      noPermission: 'Vous n\'avez pas la permission de générer des bulletins',
      republic: 'RÉPUBLIQUE DU CAMEROUN',
      republicEn: 'REPUBLIC OF CAMEROON',
      motto: 'Paix-Travail-Patrie',
      mottoEn: 'Peace-Work-Fatherland',
      reportCard: 'BULLETIN DE NOTES',
      reportCardSequence: 'BULLETIN DE NOTES — SÉQUENCE',
      reportCardTrimester: 'BULLETIN DE NOTES — TRIMESTRE',
      reportCardAnnual: 'BULLETIN DE NOTES — ANNUEL',
      studentInfo: "Informations de l\'élève",
      name: 'Noms et Prénoms',
      dob: 'Date de naissance',
      pob: 'Lieu de naissance',
      gender: 'Sexe',
      classLabel: 'Classe',
      matriculeLabel: 'Matricule',
      situation: 'Situation',
      newStudent: 'Nouveau',
      repeating: 'Redoublant',
      subjects: 'MATIÈRES',
      seq: 'Séq.',
      coef: 'Coef.',
      note: 'Note',
      avg: 'Moy.',
      pts: 'Pts',
      appreciation: 'Appréciation',
      min: 'Min',
      max: 'Max',
      classAvg: 'Moy. Classe',
      summary: 'RÉCAPITULATIF',
      totalPoints: 'Total Points',
      totalCoef: 'Total Coef.',
      generalAvg: 'Moy. Gén.',
      workAppreciation: 'Appréciation Travail',
      conduct: 'CONDUITE',
      absences: 'Absences',
      unjustified: 'Non just.',
      detentions: 'Consignes',
      exclusions: 'Exclusions',
      conductNote: 'Note Conduite',
      council: 'CONSEIL DE CLASSE',
      th: 'Tableau d\'honneur',
      enc: 'Encouragements',
      fel: 'Félicitations',
      blameWork: 'Blâme travail',
      blameConduct: 'Blâme conduite',
      warningWork: 'Avert. travail',
      warningConduct: 'Avert. conduite',
      observations: 'Observations du conseil',
      principalSignature: 'Visa du Chef d\'établissement',
      teacherSignature: 'Prof. Titulaire',
      decisionLabel: 'Décision',
      admittedTo: 'Admis en',
    },
    en: {
      title: 'Report Cards',
      subtitle: 'Generate and print student report cards',
      configTitle: 'Configuration',
      section: 'Section',
      allSections: 'All sections',
      cycle: 'Cycle',
      allCycles: 'All cycles',
      level: 'Level',
      allLevels: 'All levels',
      class: 'Class',
      allClasses: 'All classes',
      bulletinType: 'Report Card Type',
      sequence: 'Sequence',
      trimester: 'Term',
      annual: 'Annual',
      period: 'Exam Period',
      allPeriods: 'All periods',
      allSequences: 'All sequences',
      studentFilters: 'Student Filters',
      enrolled: 'Enrolled',
      excluded: 'Excluded',
      resigned: 'Resigned',
      decisions: 'Council Decisions',
      admitted: 'Admitted',
      repeated: 'Repeated',
      demoted: 'Demoted',
      expelled: 'Expelled',
      noDecision: 'No decision',
      headerTitle: 'School Header',
      headerDesc: 'Customize the header that appears on each report card',
      schoolName: 'School Name',
      schoolNameEn: 'Name (EN)',
      address: 'Address',
      poBox: 'P.O. Box',
      region: 'Region',
      department: 'Department',
      delegation: 'Delegation',
      subDivision: 'Sub Division',
      logo: 'Logo (URL)',
      saveHeader: 'Save Header',
      refresh: 'Refresh',
      downloadPDF: 'Download PDF',
      downloadExcel: 'Download Excel',
      downloadExcelData: 'Excel Data',
      generateAll: 'Generate All',
      generating: 'Generating...',
      sortBy: 'Sort by',
      sortMerit: 'Merit (average)',
      sortAlpha: 'Alphabetical',
      matricule: 'ID',
      student: 'Student',
      average: 'Average',
      rank: 'Rank',
      decision: 'Decision',
      actions: 'Actions',
      view: 'View',
      print: 'Print',
      previewTitle: 'Report Card Preview',
      closePreview: 'Close',
      noYearSelected: 'No academic year selected',
      yearRequired: 'Please select an academic year in the header',
      noData: 'Select a class to see report cards',
      noStudents: 'No students found',
      loading: 'Loading...',
      error: 'Error',
      success: 'Success',
      noPermission: 'You do not have permission to generate report cards',
      republic: 'REPUBLIC OF CAMEROON',
      republicEn: 'RÉPUBLIQUE DU CAMEROUN',
      motto: 'Peace-Work-Fatherland',
      mottoEn: 'Paix-Travail-Patrie',
      reportCard: 'REPORT CARD',
      reportCardSequence: 'REPORT CARD — SEQUENCE',
      reportCardTrimester: 'REPORT CARD — TERM',
      reportCardAnnual: 'REPORT CARD — ANNUAL',
      studentInfo: 'Student Information',
      name: 'Name',
      dob: 'Date of birth',
      pob: 'Place of birth',
      gender: 'Gender',
      classLabel: 'Class',
      matriculeLabel: 'ID',
      situation: 'Status',
      newStudent: 'New',
      repeating: 'Repeating',
      subjects: 'SUBJECTS',
      seq: 'Seq.',
      coef: 'Coef.',
      note: 'Grade',
      avg: 'Avg',
      pts: 'Pts',
      appreciation: 'Appreciation',
      min: 'Min',
      max: 'Max',
      classAvg: 'Class Avg',
      summary: 'SUMMARY',
      totalPoints: 'Total Points',
      totalCoef: 'Total Coef.',
      generalAvg: 'Gen. Avg',
      workAppreciation: 'Work Appreciation',
      conduct: 'CONDUCT',
      absences: 'Absences',
      unjustified: 'Unjust.',
      detentions: 'Detentions',
      exclusions: 'Exclusions',
      conductNote: 'Conduct Note',
      council: 'CLASS COUNCIL',
      th: 'Honor Board',
      enc: 'Encouragements',
      fel: 'Congratulations',
      blameWork: 'Blame work',
      blameConduct: 'Blame conduct',
      warningWork: 'Warning work',
      warningConduct: 'Warning conduct',
      observations: 'Council observations',
      principalSignature: 'Principal Signature',
      teacherSignature: 'Class Teacher',
      decisionLabel: 'Decision',
      admittedTo: 'Admitted to',
    }
  }[lang];

  // ==================== STATE ====================
  const [sections, setSections] = useState<any[]>([]);
  const [cycles, setCycles] = useState<any[]>([]);
  const [levels, setLevels] = useState<any[]>([]);
  const [classes, setClasses] = useState<any[]>([]);
  const [periods, setPeriods] = useState<any[]>([]);
  const [sequences, setSequences] = useState<any[]>([]);

  const [selectedSection, setSelectedSection] = useState<string>("");
  const [selectedCycle, setSelectedCycle] = useState<string>("");
  const [selectedLevel, setSelectedLevel] = useState<string>("");
  const [selectedClass, setSelectedClass] = useState<string>("");
  const [selectedPeriod, setSelectedPeriod] = useState<string>("");
  const [selectedSequence, setSelectedSequence] = useState<string>("");
  const [bulletinType, setBulletinType] = useState<'sequence' | 'trimester' | 'annual'>('annual');

  const [filterEnrolled, setFilterEnrolled] = useState(true);
  const [filterExcluded, setFilterExcluded] = useState(false);
  const [filterResigned, setFilterResigned] = useState(false);

  const [decisionAdmitted, setDecisionAdmitted] = useState(true);
  const [decisionRepeated, setDecisionRepeated] = useState(true);
  const [decisionDemoted, setDecisionDemoted] = useState(true);
  const [decisionExpelled, setDecisionExpelled] = useState(true);
  const [decisionNone, setDecisionNone] = useState(true);

  const [sortBy, setSortBy] = useState<'merit' | 'alphabetical'>('merit');
  const [bulletins, setBulletins] = useState<BulletinData[]>([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState<Record<number, boolean>>({});

  const [previewBulletin, setPreviewBulletin] = useState<BulletinData | null>(null);
  const [showPreview, setShowPreview] = useState(false);

  const [showHeaderConfig, setShowHeaderConfig] = useState(false);
  const [schoolHeader, setSchoolHeader] = useState<SchoolHeader>({
    republic_fr: 'RÉPUBLIQUE DU CAMEROUN',
    republic_en: 'REPUBLIC OF CAMEROON',
    motto_fr: 'Paix-Travail-Patrie',
    motto_en: 'Peace-Work-Fatherland',
    region: "RÉGION DE L'OUEST",
    department: 'DÉPARTEMENT DE LA MIFI',
    delegation: 'DÉLÉGATION DÉPARTEMENTALE DES ENSEIGNEMENTS SECONDAIRES',
    sub_division: 'ARRONDISSEMENT DE BAFOUSSAM II',
    school_name: 'LYCÉE BILINGUE DE BALENG',
    school_name_en: 'GBHS BALENG',
    address: 'BP : 1041 Baleng',
    po_box: 'P.O. Box : 1041 Baleng',
    logo_url: '',
  });

  const [searchQuery, setSearchQuery] = useState("");
  // Profil de la classe calculé sur TOUS les bulletins chargés.
  // ⚠️ La moyenne de CHAQUE élève est recalculée avec la MÊME règle que le
  // bulletin affiché (moyennes de matières vérifiées, coef des non-notés exclus) —
  // la valeur backend summary.general_average peut différer (ex. élève à 7.00
  // affiché mais 11.00 côté backend) et fausserait min/max et le taux.
  const officialStats = useMemo(() => {
    const computeMoy = (b: BulletinData): number => {
      let sc = 0, snx = 0;
      (b.subjects || []).forEach((subj: any) => {
        const coef = subj.coefficient || 0;
        if ((subj.grades_count || 0) <= 0 || coef <= 0) return;
        const m = (subj.annual_average || 0) > 0 ? subj.annual_average
          : (subj.display_note || 0) > 0 ? subj.display_note
          : (() => { const ts = (subj.trimester_scores || []).filter((v: number) => v > 0); return ts.length ? ts.reduce((a: number, x: number) => a + x, 0) / ts.length : 0; })();
        sc += coef;
        snx += m * coef;
      });
      return sc > 0 ? snx / sc : 0;
    };
    const vals = bulletins.map(computeMoy).filter(v => v > 0);
    if (!vals.length) return null;
    return {
      min: Math.min(...vals),
      max: Math.max(...vals),
      nbMoyennes: vals.length,
      tauxReussite: Math.round((vals.filter(v => v >= 10).length / vals.length) * 100),
    };
  }, [bulletins]);

  // Contacts des parents/tuteurs (emergency_contact + emergency_phone par élève)
  const [parentContacts, setParentContacts] = useState<Map<number, string>>(new Map());
  useEffect(() => {
    if (!selectedClass) { setParentContacts(new Map()); return; }
    (async () => {
      try {
        const res: any = await apiService.get('/students', {
          params: { class_id: selectedClass, per_page: 500, school_id: schoolId || undefined },
        }).catch(() => null);
        const rows = extractData(res);
        const map = new Map<number, string>();
        rows.forEach((st: any) => {
          const parts = [st.emergency_contact, st.emergency_phone].filter(Boolean);
          if (st.id && parts.length) map.set(Number(st.id), parts.join(' — '));
        });
        setParentContacts(map);
      } catch { /* ignore */ }
    })();
  }, [selectedClass, schoolId]);

  // Descriptions RÉELLES des compétences (champ "description" de /competencies) —
  // le bulletin ne fournit qu'un nom généré ("FRANCAIS Trimestre 3 Competence 1").
  const [compDescriptions, setCompDescriptions] = useState<Map<number, string>>(new Map());
  const [selectedClassSchoolId, setSelectedClassSchoolId] = useState<number | null>(null);
  const [lastClassInfo, setLastClassInfo] = useState<any>(null);
  const [rawStudents, setRawStudents] = useState<any[]>([]);

  // ==================== DATA LOADING ====================
  const getFilterParams = useCallback(() => {
    const params: Record<string, any> = {};
    if (schoolId) params.school_id = schoolId;
    if (selectedAcademicYearId) params.academic_year_id = selectedAcademicYearId;
    return params;
  }, [schoolId, selectedAcademicYearId]);

  // Params pour les appels liés à une classe spécifique (utilise le school_id de la classe)
  const getClassApiParams = useCallback(() => {
    const params: Record<string, any> = {};
    const effectiveSchoolId = selectedClassSchoolId || schoolId;
    if (effectiveSchoolId) params.school_id = effectiveSchoolId;
    if (selectedAcademicYearId) params.academic_year_id = selectedAcademicYearId;
    return params;
  }, [selectedClassSchoolId, schoolId, selectedAcademicYearId]);

  // Load sections, cycles, levels, periods, sequences
  useEffect(() => {
    const loadConfig = async () => {
      if (!selectedAcademicYearId) return;
      try {
        const filterParams = getFilterParams();
        const isPrivileged = normalizeRole(roleName) === 'superadmin' || normalizeRole(roleName) === 'admin' || normalizeRole(roleName) === 'proviseur' || normalizeRole(roleName) === 'censeur';

        const [secRes, cycRes, levRes, perRes, seqRes] = await Promise.all([
          loadWithFallback('/sections', filterParams, isPrivileged),
          loadWithFallback('/cycles', filterParams, isPrivileged),
          loadWithFallback('/levels', filterParams, isPrivileged),
          loadWithFallback('/grades/periods', filterParams, isPrivileged),
          loadWithFallback('/grades/sequences', filterParams, isPrivileged),
        ]);

        setSections(secRes);
        setCycles(cycRes);
        setLevels(levRes);
        setPeriods(perRes);
        setSequences(seqRes);
      } catch (e) {
        console.error('Config load error:', e);
      }
    };
    loadConfig();
  }, [schoolId, selectedAcademicYearId, getFilterParams, roleName]);

  // Load classes — RECHARGÉ quand section/cycle/level change, avec filtres backend
  const loadClasses = useCallback(async () => {
    if (!selectedAcademicYearId) {
      setClasses([]);
      setSelectedClass("");
      return;
    }
    try {
      const params: Record<string, any> = {
        academic_year_id: selectedAcademicYearId,
      };
      if (schoolId) params.school_id = schoolId;
      // Toujours envoyer level_id quand un niveau est sélectionné
      if (selectedLevel) params.level_id = parseInt(selectedLevel);

      console.log('[DEBUG] Loading classes with params:', params);
      const isPrivileged = normalizeRole(roleName) === 'superadmin' || normalizeRole(roleName) === 'admin' || normalizeRole(roleName) === 'proviseur' || normalizeRole(roleName) === 'censeur';
      let data = await loadWithFallback('/classes', params, isPrivileged);
      console.log('[DEBUG] Classes loaded (raw):', data.length, data);

      // Filtrage frontend de sécurité (robuste string/number) — ne filtre que si level_id existe sur la classe
      if (selectedLevel) {
        const levelIdNum = parseInt(selectedLevel);
        data = data.filter((c: any) => {
          const cid = c.level_id != null ? Number(c.level_id) : (c.levelId != null ? Number(c.levelId) : null);
          // Si la classe n'a pas de level_id, on la garde (ne pas exclure à cause d'un champ manquant)
          return cid === null || cid === levelIdNum;
        });
        console.log('[DEBUG] Classes after filter:', data.length, data);
      }

      setClasses(data);
      // Si la classe sélectionnée n'est plus dans la liste, la vider
      if (selectedClass && !data.find((c: any) => String(c.id) === selectedClass)) {
        setSelectedClass("");
        setSelectedClassSchoolId(null);
      }
    } catch (e: any) {
      console.error('Classes load error:', e);
      console.error('Response:', e?.response?.data);
      setClasses([]);
      setSelectedClass("");
      setSelectedClassSchoolId(null);
    }
  }, [schoolId, selectedAcademicYearId, selectedLevel, roleName, selectedClass]);

  useEffect(() => {
    loadClasses();
  }, [loadClasses]);

  // Load bulletins when class/period/type changes
  const loadBulletins = useCallback(async () => {
    if (!selectedClass || !selectedAcademicYearId) {
      setBulletins([]);
      return;
    }
    setLoading(true);
    try {
      const filterParams = getClassApiParams();
      const params: any = {
        ...filterParams,
        sort_by: sortBy,
        bulletin_type: bulletinType,
      };
      if (bulletinType === 'sequence' && selectedSequence) {
        params.sequence_id = parseInt(selectedSequence);
      }
      if ((bulletinType === 'trimester') && selectedPeriod) {
        params.evaluation_period_id = parseInt(selectedPeriod);
      }

      console.log('[DEBUG] Loading bulletins for class', selectedClass, 'with params:', params);

      const isPrivileged = normalizeRole(roleName) === 'superadmin' || normalizeRole(roleName) === 'admin' || normalizeRole(roleName) === 'proviseur' || normalizeRole(roleName) === 'censeur';
      let res = await apiService.get(`/bulletins/class/${selectedClass}`, { params }).catch((err) => {
        console.error('[DEBUG] API error:', err?.response?.status, err?.response?.data);
        return null;
      });

      let data = (res as any)?.data ?? res;
      console.log('[DEBUG] Bulletins raw response:', data);

      // Fallback sans school_id si admin et résultat vide
      if ((!data || (Array.isArray(data.bulletins) && data.bulletins.length === 0) || (Array.isArray(data) && data.length === 0)) && params.school_id && isPrivileged) {
        const { school_id, ...paramsNoSchool } = params;
        res = await apiService.get(`/bulletins/class/${selectedClass}`, { params: paramsNoSchool }).catch((err) => {
          console.error('[DEBUG] Fallback API error:', err?.response?.status, err?.response?.data);
          return null;
        });
        data = (res as any)?.data ?? res;
        console.log('[DEBUG] Bulletins fallback response:', data);
      }

      // CORRECTION: gère les deux formats de réponse { bulletins: [] } et []
      let bulletinsArray: BulletinData[] = [];
      let classInfo = null;
      if (data && Array.isArray(data.bulletins)) {
        bulletinsArray = data.bulletins;
        classInfo = data.class_info || null;
      } else if (Array.isArray(data)) {
        bulletinsArray = data;
      } else if (data && typeof data === 'object') {
        const possibleArrays = Object.values(data).filter(v => Array.isArray(v));
        if (possibleArrays.length > 0) {
          bulletinsArray = possibleArrays[0] as BulletinData[];
        }
        classInfo = data.class_info || null;
      }

      console.log('[DEBUG] Final bulletins array:', bulletinsArray.length, 'classInfo:', classInfo);
      setBulletins(bulletinsArray);
      setLastClassInfo(classInfo);

      // FALLBACK 1: si bulletins vides en mode sequence/trimester, essayer annual
      if (bulletinsArray.length === 0 && selectedClass && bulletinType !== 'annual') {
        try {
          const annualParams = { ...filterParams, sort_by: sortBy, bulletin_type: 'annual' };
          const annualRes = await apiService.get(`/bulletins/class/${selectedClass}`, { params: annualParams }).catch(() => null);
          const annualData = annualRes ? ((annualRes as any)?.data ?? annualRes) : null;
          if (annualData && Array.isArray(annualData.bulletins) && annualData.bulletins.length > 0) {
            console.log('[DEBUG] Fallback annual bulletins:', annualData.bulletins.length);
            setBulletins(annualData.bulletins);
            setLastClassInfo(annualData.class_info || classInfo);
            toast.info(lang === 'fr' ? 'Bulletins annuels affichés (aucun bulletin pour la période sélectionnée)' : 'Annual report cards displayed (none for selected period)');
            setRawStudents([]);
            setLoading(false);
            return;
          }
        } catch (e) {
          console.warn('[DEBUG] Fallback annual failed:', e);
        }
      }

      // FALLBACK 2: si toujours vides, charger les élèves de la classe directement
      if (bulletinsArray.length === 0 && selectedClass) {
        try {
          const studentsRes = await apiService.get('/students', {
            params: { class_id: selectedClass, school_id: schoolId || undefined }
          }).catch(() => null);
          const studentsData = studentsRes ? extractData(studentsRes) : [];
          console.log('[DEBUG] Fallback students loaded:', studentsData.length);
          setRawStudents(studentsData);
        } catch (e) {
          console.warn('[DEBUG] Fallback students load failed:', e);
          setRawStudents([]);
        }
      } else {
        setRawStudents([]);
      }
    } catch (err: any) {
      console.error('Load bulletins error:', err);
      toast.error(t.error + ': ' + (err?.response?.data?.detail || err?.message || 'Unknown'));
      setBulletins([]);
    } finally {
      setLoading(false);
    }
  }, [selectedClass, selectedPeriod, selectedSequence, bulletinType, sortBy, schoolId, selectedAcademicYearId, getClassApiParams, roleName]);

  useEffect(() => {
    if (selectedClass) loadBulletins();
  }, [selectedClass, selectedPeriod, selectedSequence, bulletinType, sortBy, loadBulletins]);

  // Charger les vraies descriptions des compétences du niveau de la classe
  useEffect(() => {
    if (!selectedClass) { setCompDescriptions(new Map()); return; }
    (async () => {
      try {
        const clsRes: any = await apiService.get(`/classes/${selectedClass}`).catch(() => null);
        const levelId = clsRes?.level_id ?? clsRes?.data?.level_id;
        if (!levelId) return;
        const res: any = await apiService.get('/competencies', { params: { level_id: levelId } }).catch(() => null);
        const rows = extractData(res);
        const map = new Map<number, string>();
        rows.forEach((c: any) => {
          const desc = c.description || c.competence_description || c.label_fr || '';
          if (c.id != null && desc) map.set(Number(c.id), String(desc));
        });
        if (map.size > 0) setCompDescriptions(map);
      } catch { /* ignore */ }
    })();
  }, [selectedClass]);

  // Filter bulletins
  const filteredBulletins = useMemo(() => {
    let filtered = [...bulletins];

    // Filtre de statut : si tous les filtres sont cochés, ne pas filtrer
    const allStatusFilters = filterEnrolled && filterExcluded && filterResigned;
    if (!allStatusFilters) {
      filtered = filtered.filter(b => {
        const status = (b.student.enrollment_status || 'active').toLowerCase();
        if (status === 'active' || status === 'enrolled' || status === 'inscrit') return filterEnrolled;
        if (status === 'excluded' || status === 'exclu') return filterExcluded;
        if (status === 'resigned' || status === 'dropped' || status === 'demissionnaire') return filterResigned;
        return filterEnrolled; // statut inconnu = traité comme inscrit
      });
    }

    // Filtre de décisions : si tous les filtres sont cochés, ne pas filtrer
    const allDecisionFilters = decisionAdmitted && decisionRepeated && decisionDemoted && decisionExpelled && decisionNone;
    if (!allDecisionFilters) {
      filtered = filtered.filter(b => {
        const decision = (b.student as any).decision?.toLowerCase() || '';
        if (!decision || decision === '') return decisionNone;
        if (decision.includes('admi')) return decisionAdmitted;
        if (decision.includes('redoubl')) return decisionRepeated;
        if (decision.includes('retrograd')) return decisionDemoted;
        if (decision.includes('exclu')) return decisionExpelled;
        return decisionNone; // décision inconnue = traitée comme aucune
      });
    }

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(b =>
        b.student.full_name?.toLowerCase().includes(q) ||
        b.student.matricule?.toLowerCase().includes(q)
      );
    }

    return filtered;
  }, [bulletins, filterEnrolled, filterExcluded, filterResigned, decisionAdmitted, decisionRepeated, decisionDemoted, decisionExpelled, decisionNone, searchQuery]);

  const handlePreview = (bulletin: BulletinData) => {
    setPreviewBulletin(bulletin);
    setShowPreview(true);
  };

  const handleDownloadPDF = async (bulletin: BulletinData) => {
    if (!canGenerateBulletins) {
      toast.error(t.noPermission);
      return;
    }
    setGenerating(prev => ({ ...prev, [bulletin.student.id]: true }));
    try {
      // ✅ PDF = fenêtre d'impression (« Enregistrer au format PDF » comme
      // destination) — génération à la volée, jamais de fichier .html figé.
      openBulletinPrint(bulletinHTMLFor(bulletin));
      toast.success(t.success);
    } catch (err) {
      toast.error(t.error);
    } finally {
      setGenerating(prev => ({ ...prev, [bulletin.student.id]: false }));
    }
  };

  // Nouveau modèle officiel : UNIQUEMENT 1er cycle (approche par compétences),
  // trimestre ET annuel. Le 2nd cycle conserve la présentation existante.
  const bulletinHTMLFor = (b: BulletinData): string =>
    (b.bulletin_type !== 'sequence' && b.is_competence_approach)
      ? generateBulletinOfficialHTML(b, schoolHeader, lang, compDescriptions, officialStats, parentContacts)
      : generateBulletinHTML(b, schoolHeader, lang);

  // Fenêtre d'impression commune (bulletin unique ou classe entière)
  const openBulletinPrint = (html: string) => {
    const pw = window.open('', '_blank');
    if (!pw) { toast.error(lang === 'fr' ? 'Autorisez les popups' : 'Allow popups'); return; }
    pw.document.write(html);
    pw.document.close();
    pw.focus();
    setTimeout(() => { try { pw.print(); } catch { /* ignore */ } }, 400);
  };

  const handleDownloadAllPDF = async () => {
    if (!canGenerateBulletins) {
      toast.error(t.noPermission);
      return;
    }
    if (!filteredBulletins.length) {
      toast.info(lang === 'fr' ? 'Aucun bulletin à télécharger' : 'No report cards to download');
      return;
    }
    setLoading(true);
    toast.info(lang === 'fr' ? `Génération de ${filteredBulletins.length} bulletin(s)...` : `Generating ${filteredBulletins.length} report card(s)...`);
    try {
      const allHtml = filteredBulletins.map((b, i) => {
        const html = bulletinHTMLFor(b);
        const separator = i < filteredBulletins.length - 1
          ? '<div style="page-break-after:always;"></div>'
          : '';
        return html + separator;
      }).join('\n');
      const fullDoc = `<!DOCTYPE html>
<html>
<head><meta charset="UTF-8"><title>Bulletins ${selectedClass}</title></head>
<body>${allHtml}</body>
</html>`;
      openBulletinPrint(fullDoc);
      toast.success(lang === 'fr' ? 'Impression de tous les bulletins lancée' : 'Printing all report cards');
    } catch (err) {
      toast.error(t.error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadExcel = () => {
    if (!canGenerateBulletins) {
      toast.error(t.noPermission);
      return;
    }
    if (!filteredBulletins.length) return;
    const headers = [
      'Matricule', 'Nom', 'Prénom', 'Classe', 'Moyenne', 'Rang', 'Décision'
    ];
    const rows = filteredBulletins.map(b => [
      b.student.matricule,
      b.student.last_name,
      b.student.first_name,
      b.student.class_name,
      b.summary.general_average.toFixed(2),
      b.summary.rank,
      (b.student as any).decision || '-'
    ]);
    const csv = [headers.join(';'), ...rows.map(r => r.join(';'))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bulletins_${selectedClass}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(t.success);
  };

  const handleSaveHeader = () => {
    localStorage.setItem('lybibal-school-header', JSON.stringify(schoolHeader));
    toast.success(t.success);
  };

  useEffect(() => {
    try {
      const saved = localStorage.getItem('lybibal-school-header');
      if (saved) setSchoolHeader(JSON.parse(saved));
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => {
    const yearId = getSelectedYearId();
    const yearLabel = localStorage.getItem(SELECTED_YEAR_LABEL_KEY);
    if (yearId) {
      setSelectedAcademicYearId(yearId);
      if (yearLabel) setCurrentAcademicYear({ id: yearId, name: yearLabel });
    }
    try {
      const storedSchool = localStorage.getItem('lybibal-selected-school-id');
      if (storedSchool) setSchoolId(Number(storedSchool));
    } catch (err) { /* ignore */ }
  }, []);

  useEffect(() => {
    const handleYearChange = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.year_id) {
        setSelectedAcademicYearId(Number(detail.year_id));
        if (detail.label) setCurrentAcademicYear({ id: Number(detail.year_id), name: detail.label });
        setSelectedClass("");
        setSelectedClassSchoolId(null);
        setBulletins([]);
      }
    };
    window.addEventListener(SELECTED_YEAR_EVENT, handleYearChange);
    return () => window.removeEventListener(SELECTED_YEAR_EVENT, handleYearChange);
  }, []);

  // Séquences filtrées par période
  const periodOptions = useMemo(() => {
    if (bulletinType === 'sequence') {
      return sequences.map((s: any) => ({
        id: s.id,
        label: s.label || s.name || `Séquence ${s.id}`,
        isSequence: true,
      }));
    }
    return periods.map((p: any) => ({
      id: p.id,
      label: p.label_fr || p.label_en || `Période ${p.id}`,
      isSequence: false,
    }));
  }, [bulletinType, sequences, periods]);

  if (!selectedAcademicYearId) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center max-w-md">
          <Calendar className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">{t.noYearSelected}</h2>
          <p className="text-sm text-gray-500">{t.yearRequired}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <FileText className="w-7 h-7 text-[#1e3a8a]" />
          {t.title}
        </h1>
        <p className="text-sm text-gray-500 mt-1">{t.subtitle}</p>
        {currentAcademicYear && (
          <div className="flex items-center gap-2 mt-2">
            <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
              {currentAcademicYear.name}
            </span>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* ===== LEFT PANEL: Configuration ===== */}
        <div className="xl:col-span-1 space-y-4">
          {/* Configuration */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
            <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2 mb-4">
              <Settings className="w-4 h-4 text-[#1e3a8a]" />
              {t.configTitle}
            </h3>
            <div className="space-y-3">
              {/* Section */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{t.section}</label>
                <select
                  value={selectedSection}
                  onChange={(e) => { setSelectedSection(e.target.value); setSelectedCycle(""); setSelectedLevel(""); setSelectedClass(""); setSelectedClassSchoolId(null); setClasses([]); }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#1e3a8a]"
                >
                  <option value="">{t.allSections}</option>
                  {sections.map((s: any) => (
                    <option key={s.id} value={String(s.id)}>{s.name || s.label_fr}</option>
                  ))}
                </select>
              </div>
              {/* Cycle */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{t.cycle}</label>
                <select
                  value={selectedCycle}
                  onChange={(e) => { setSelectedCycle(e.target.value); setSelectedLevel(""); setSelectedClass(""); setSelectedClassSchoolId(null); setClasses([]); }}
                  disabled={!selectedSection}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#1e3a8a] disabled:bg-gray-100 disabled:cursor-not-allowed"
                >
                  <option value="">{t.allCycles}</option>
                  {cycles.filter((c: any) => !selectedSection || String(c.section_id) === selectedSection).map((c: any) => (
                    <option key={c.id} value={String(c.id)}>{c.name || c.label_fr}</option>
                  ))}
                </select>
              </div>
              {/* Level */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{t.level}</label>
                <select
                  value={selectedLevel}
                  onChange={(e) => { setSelectedLevel(e.target.value); setSelectedClass(""); setSelectedClassSchoolId(null); setClasses([]); }}
                  disabled={!selectedCycle}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#1e3a8a] disabled:bg-gray-100 disabled:cursor-not-allowed"
                >
                  <option value="">{t.allLevels}</option>
                  {levels.filter((l: any) => !selectedCycle || String(l.cycle_id) === selectedCycle).map((l: any) => (
                    <option key={l.id} value={String(l.id)}>{l.name || l.label_fr}</option>
                  ))}
                </select>
              </div>
              {/* Class */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{t.class}</label>
                <select
                  value={selectedClass}
                  onChange={(e) => {
                    const classId = e.target.value;
                    setSelectedClass(classId);
                    const cls = classes.find((c: any) => String(c.id) === classId);
                    setSelectedClassSchoolId(cls?.school_id || null);
                  }}
                  disabled={!selectedLevel}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#1e3a8a] disabled:bg-gray-100 disabled:cursor-not-allowed"
                >
                  <option value="">{t.allClasses}</option>
                  {classes
                    .filter((c: any) => {
                      // Si un cycle est sélectionné mais pas de niveau, filtrer par cycle via level_id
                      if (!selectedLevel && selectedCycle) {
                        const level = levels.find((l: any) => String(l.id) === String(c.level_id));
                        return level && String(level.cycle_id) === selectedCycle;
                      }
                      return !selectedLevel || String(c.level_id) === selectedLevel;
                    })
                    .map((c: any) => (
                      <option key={c.id} value={String(c.id)}>{c.name}</option>
                    ))}
                </select>
                {selectedLevel && classes.length === 0 && (
                  <p className="text-xs text-orange-600 mt-1">
                    {lang === 'fr'
                      ? 'Aucune classe trouvée pour ce niveau. Vérifiez que les classes ont un level_id en base.'
                      : 'No classes found for this level. Check that classes have a level_id in the database.'}
                  </p>
                )}
              </div>

              {/* Type de bulletin */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{t.bulletinType}</label>
                <select
                  value={bulletinType}
                  onChange={(e) => {
                    setBulletinType(e.target.value as 'sequence' | 'trimester' | 'annual');
                    setSelectedPeriod("");
                    setSelectedSequence("");
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#1e3a8a]"
                >
                  <option value="annual">{t.annual}</option>
                  <option value="trimester">{t.trimester}</option>
                  <option value="sequence">{t.sequence}</option>
                </select>
              </div>

              {/* Période / Séquence */}
              {bulletinType !== 'annual' && (
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    {bulletinType === 'sequence' ? t.sequence : t.period}
                  </label>
                  <select
                    value={bulletinType === 'sequence' ? selectedSequence : selectedPeriod}
                    onChange={(e) => {
                      if (bulletinType === 'sequence') {
                        setSelectedSequence(e.target.value);
                      } else {
                        setSelectedPeriod(e.target.value);
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#1e3a8a]"
                  >
                    <option value="">
                      {bulletinType === 'sequence' ? t.allSequences : t.allPeriods}
                    </option>
                    {periodOptions.map((opt: any) => (
                      <option key={opt.id} value={String(opt.id)}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </div>

          {/* Student Filters */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
            <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2 mb-4">
              <Filter className="w-4 h-4 text-[#1e3a8a]" />
              {t.studentFilters}
            </h3>
            <div className="space-y-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={filterEnrolled} onChange={(e) => setFilterEnrolled(e.target.checked)} className="w-4 h-4 text-[#1e3a8a] rounded" />
                <span className="text-sm text-gray-700">{t.enrolled}</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={filterExcluded} onChange={(e) => setFilterExcluded(e.target.checked)} className="w-4 h-4 text-[#1e3a8a] rounded" />
                <span className="text-sm text-gray-700">{t.excluded}</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={filterResigned} onChange={(e) => setFilterResigned(e.target.checked)} className="w-4 h-4 text-[#1e3a8a] rounded" />
                <span className="text-sm text-gray-700">{t.resigned}</span>
              </label>
            </div>
          </div>

          {/* Decisions */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
            <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2 mb-4">
              <GraduationCap className="w-4 h-4 text-[#1e3a8a]" />
              {t.decisions}
            </h3>
            <div className="space-y-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={decisionAdmitted} onChange={(e) => setDecisionAdmitted(e.target.checked)} className="w-4 h-4 text-emerald-600 rounded" />
                <span className="text-sm text-gray-700">{t.admitted}</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={decisionRepeated} onChange={(e) => setDecisionRepeated(e.target.checked)} className="w-4 h-4 text-amber-600 rounded" />
                <span className="text-sm text-gray-700">{t.repeated}</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={decisionDemoted} onChange={(e) => setDecisionDemoted(e.target.checked)} className="w-4 h-4 text-orange-600 rounded" />
                <span className="text-sm text-gray-700">{t.demoted}</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={decisionExpelled} onChange={(e) => setDecisionExpelled(e.target.checked)} className="w-4 h-4 text-red-600 rounded" />
                <span className="text-sm text-gray-700">{t.expelled}</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={decisionNone} onChange={(e) => setDecisionNone(e.target.checked)} className="w-4 h-4 text-gray-600 rounded" />
                <span className="text-sm text-gray-700">{t.noDecision}</span>
              </label>
            </div>
          </div>

          {/* School Header Config */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
            <button
              onClick={() => setShowHeaderConfig(!showHeaderConfig)}
              className="flex items-center justify-between w-full text-sm font-semibold text-gray-900"
            >
              <span className="flex items-center gap-2">
                <Building2 className="w-4 h-4 text-[#1e3a8a]" />
                {t.headerTitle}
              </span>
              {showHeaderConfig ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
            {showHeaderConfig && (
              <div className="mt-4 space-y-3">
                <p className="text-xs text-gray-500">{t.headerDesc}</p>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">{t.schoolName}</label>
                  <input value={schoolHeader.school_name} onChange={(e) => setSchoolHeader(p => ({ ...p, school_name: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">{t.schoolNameEn}</label>
                  <input value={schoolHeader.school_name_en} onChange={(e) => setSchoolHeader(p => ({ ...p, school_name_en: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">{t.region}</label>
                  <input value={schoolHeader.region} onChange={(e) => setSchoolHeader(p => ({ ...p, region: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">{t.department}</label>
                  <input value={schoolHeader.department} onChange={(e) => setSchoolHeader(p => ({ ...p, department: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">{t.delegation}</label>
                  <input value={schoolHeader.delegation} onChange={(e) => setSchoolHeader(p => ({ ...p, delegation: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">{t.subDivision}</label>
                  <input value={schoolHeader.sub_division} onChange={(e) => setSchoolHeader(p => ({ ...p, sub_division: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">{t.address}</label>
                  <input value={schoolHeader.address} onChange={(e) => setSchoolHeader(p => ({ ...p, address: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">{t.poBox}</label>
                  <input value={schoolHeader.po_box} onChange={(e) => setSchoolHeader(p => ({ ...p, po_box: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">{t.logo}</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        const reader = new FileReader();
                        reader.onloadend = () => {
                          setSchoolHeader(p => ({ ...p, logo_url: reader.result as string }));
                        };
                        reader.readAsDataURL(file);
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm file:mr-3 file:py-1 file:px-3 file:rounded file:border-0 file:text-xs file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  />
                  {schoolHeader.logo_url && (
                    <div className="mt-2 flex items-center gap-2">
                      <img src={schoolHeader.logo_url} alt="Logo" className="w-12 h-12 object-contain border rounded" />
                      <button
                        onClick={() => setSchoolHeader(p => ({ ...p, logo_url: '' }))}
                        className="text-xs text-red-600 hover:text-red-800 underline"
                      >
                        {lang === 'fr' ? 'Supprimer' : 'Remove'}
                      </button>
                    </div>
                  )}
                </div>
                <button
                  onClick={handleSaveHeader}
                  className="w-full px-4 py-2 bg-[#1e3a8a] text-white rounded-lg text-sm font-medium hover:bg-[#152a5e] flex items-center justify-center gap-2"
                >
                  <Save className="w-4 h-4" />
                  {t.saveHeader}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* ===== RIGHT PANEL: Students List + Actions ===== */}
        <div className="xl:col-span-2 space-y-4">
          {/* Permission banner */}
          {!canGenerateBulletins && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-center gap-2">
              <Lock className="w-4 h-4 text-amber-600" />
              <span className="text-xs text-amber-700">{t.noPermission} (role: {roleName || '?'})</span>
            </div>
          )}

          {/* Action Bar */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex flex-wrap items-center gap-3">
            <button
              onClick={loadBulletins}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 flex items-center gap-2 transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              {t.refresh}
            </button>
            <div className="h-6 w-px bg-gray-200" />
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as 'merit' | 'alphabetical')}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#1e3a8a]"
            >
              <option value="merit">{t.sortMerit}</option>
              <option value="alphabetical">{t.sortAlpha}</option>
            </select>
            <div className="flex-1" />
            <div className="relative">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                placeholder={lang === 'fr' ? 'Rechercher...' : 'Search...'}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#1e3a8a] w-48"
              />
            </div>
            <div className="h-6 w-px bg-gray-200" />
            <button
              onClick={handleDownloadAllPDF}
              disabled={!filteredBulletins.length || !canGenerateBulletins}
              className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50 flex items-center gap-2 transition-colors"
            >
              <Printer className="w-4 h-4" />
              {t.downloadPDF}
            </button>
            <button
              onClick={handleDownloadExcel}
              disabled={!filteredBulletins.length || !canGenerateBulletins}
              className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-2 transition-colors"
            >
              <FileSpreadsheet className="w-4 h-4" />
              {t.downloadExcel}
            </button>
          </div>

          {/* Students Table */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase w-24">{t.matricule}</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">{t.student}</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase w-24">{t.average}</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase w-20">{t.rank}</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase w-28">{t.decision}</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase w-32">{t.actions}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {loading ? (
                    <tr><td colSpan={6} className="px-4 py-12 text-center">
                      <Loader2 className="w-6 h-6 animate-spin text-gray-400 mx-auto" />
                      <p className="text-sm text-gray-500 mt-2">{t.loading}</p>
                    </td></tr>
                  ) : !selectedClass ? (
                    <tr><td colSpan={6} className="px-4 py-12 text-center text-gray-500">
                      <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p className="text-sm">{t.noData}</p>
                    </td></tr>
                  ) : filteredBulletins.length === 0 && rawStudents.length === 0 ? (
                    <tr><td colSpan={6} className="px-4 py-12 text-center text-gray-500">
                      <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p className="text-sm font-medium">{t.noStudents}</p>
                      {lastClassInfo && (
                        <p className="text-xs text-gray-400 mt-2 max-w-md mx-auto">
                          {lang === 'fr' 
                            ? `Aucun bulletin généré pour ${lastClassInfo.class_name || 'cette classe'}. Les notes existent en base mais les bulletins doivent être générés côté backend (endpoint /bulletins/generate ou calcul automatique requis).` 
                            : `No report card generated for ${lastClassInfo.class_name || 'this class'}. Grades exist in database but report cards must be generated on backend (endpoint /bulletins/generate or automatic calculation required).`}
                        </p>
                      )}
                    </td></tr>
                  ) : filteredBulletins.length === 0 && rawStudents.length > 0 ? (
                    rawStudents.map((student) => (
                      <tr key={student.id || student.matricule} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3 text-sm font-mono text-[#1e3a8a] font-medium">{student.matricule || '-'}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-[#1e3a8a]/10 flex items-center justify-center overflow-hidden flex-shrink-0">
                              {student.photo_url ? (
                                <img src={resolvePhotoUrl(student.photo_url) || ''} alt={student.full_name || student.name}
                                  className="w-full h-full object-cover"
                                  onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                />
                              ) : (
                                <span className="text-xs font-bold text-[#1e3a8a]">
                                  {(student.first_name?.[0] || '') + (student.last_name?.[0] || '')}
                                </span>
                              )}
                            </div>
                            <span className="text-sm font-medium text-gray-900">{student.full_name || `${student.last_name} ${student.first_name}`}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center"><span className="text-xs text-gray-400">—</span></td>
                        <td className="px-4 py-3 text-center text-sm text-gray-400">—</td>
                        <td className="px-4 py-3 text-center">
                          <span className="text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-600 border border-amber-200">
                            {lang === 'fr' ? 'Bulletin non généré' : 'No report card'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span className="text-xs text-gray-400 italic">{lang === 'fr' ? 'Notes saisies' : 'Grades entered'}</span>
                        </td>
                      </tr>
                    ))
                  ) : (
                    filteredBulletins.map((bulletin) => (
                      <tr key={bulletin.student.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3 text-sm font-mono text-[#1e3a8a] font-medium">{bulletin.student.matricule}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-[#1e3a8a]/10 flex items-center justify-center overflow-hidden flex-shrink-0">
                              {bulletin.student.photo_url ? (
                                <img 
                                  src={resolvePhotoUrl(bulletin.student.photo_url) || ''} 
                                  alt={bulletin.student.full_name}
                                  className="w-full h-full object-cover"
                                  onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                />
                              ) : (
                                <span className="text-xs font-bold text-[#1e3a8a]">
                                  {(bulletin.student.first_name?.[0] || '') + (bulletin.student.last_name?.[0] || '')}
                                </span>
                              )}
                            </div>
                            <span className="text-sm font-medium text-gray-900">{bulletin.student.full_name}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-bold ${getAppreciationColor(bulletin.summary.general_average)} bg-opacity-10`}>
                            {bulletin.summary.general_average.toFixed(2)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center text-sm text-gray-700 font-medium">{bulletin.summary.rank}</td>
                        <td className="px-4 py-3 text-center">
                          <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                            {(bulletin.student as any).decision || '-'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={() => handlePreview(bulletin)}
                              className="p-1.5 hover:bg-blue-50 rounded-lg text-[#1e3a8a] transition-colors"
                              title={t.view}
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDownloadPDF(bulletin)}
                              disabled={generating[bulletin.student.id] || !canGenerateBulletins}
                              className="p-1.5 hover:bg-red-50 rounded-lg text-red-600 transition-colors disabled:opacity-50"
                              title={canGenerateBulletins ? t.print : t.noPermission}
                            >
                              {generating[bulletin.student.id] ? <Loader2 className="w-4 h-4 animate-spin" /> : <Printer className="w-4 h-4" />}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {(filteredBulletins.length > 0 || rawStudents.length > 0) && (
              <div className="px-4 py-3 border-t border-gray-100 bg-gray-50 flex items-center justify-between">
                <span className="text-xs text-gray-500">
                  {filteredBulletins.length > 0 ? `${filteredBulletins.length} ${lang === 'fr' ? 'élève(s)' : 'student(s)'}` : `${rawStudents.length} ${lang === 'fr' ? 'élève(s) sans bulletin' : 'student(s) without report card'}`}
                </span>
                {filteredBulletins.length > 0 && (
                  <span className="text-xs text-gray-500">
                    {lang === 'fr' ? 'Moy. classe:' : 'Class avg:'} {(filteredBulletins.reduce((sum, b) => sum + b.summary.general_average, 0) / filteredBulletins.length).toFixed(2)}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ===== PREVIEW MODAL ===== */}
      {showPreview && previewBulletin && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Eye className="w-5 h-5 text-[#1e3a8a]" />
                {t.previewTitle}
              </h2>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleDownloadPDF(previewBulletin)}
                  disabled={!canGenerateBulletins}
                  className="px-3 py-1.5 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50 flex items-center gap-1.5"
                >
                  <Printer className="w-3.5 h-3.5" />
                  PDF
                </button>
                <button
                  onClick={() => setShowPreview(false)}
                  className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-500"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="p-6">
              {(previewBulletin.bulletin_type !== 'sequence' && previewBulletin.is_competence_approach) ? (
                <iframe
                  title="bulletin-officiel"
                  sandbox=""
                  srcDoc={generateBulletinOfficialHTML(previewBulletin, schoolHeader, lang, compDescriptions, officialStats, parentContacts)}
                  style={{ width: '100%', height: '78vh', border: '1px solid #e5e7eb', borderRadius: 8, background: '#fff' }}
                />
              ) : (
                <BulletinPreview bulletin={previewBulletin} header={schoolHeader} lang={lang} t={t} />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ==================== BULLETIN PREVIEW COMPONENT ====================
// ✅ Calculer la moyenne des compétences (1er cycle) — null ignoré, 0 compté
function calculateCompetencyAverage(competencies?: Array<{score?: number | null}>): number {
  if (!competencies || competencies.length === 0) return 0;
  const validScores = competencies
    .map(c => c.score)
    .filter((s): s is number => s !== null && s !== undefined);
  if (validScores.length === 0) return 0;
  return validScores.reduce((a, b) => a + b, 0) / validScores.length;
}

function BulletinPreview({ bulletin, header, lang: _lang, t }: { bulletin: BulletinData; header: SchoolHeader; lang: string; t: any }) {
  const student = bulletin.student;
  const ci = bulletin.class_info || { name: '', level: '', level_code: '', cycle: '', section: '' };
  const aw = bulletin.annual_work || { total_points: 0, total_coef: 0, moy_annuelle: 0, rang_annuel: 0, nbre_matieres: '-', moy_classe: 0 };
  const conduct = bulletin.conduct || { absence_hours: 0, absence_unjustified: 0, detention_days: 0, exclusion_days: 0, note: 20 };
  const council = bulletin.council || { tableau_honneur: 'NON', encouragements: 'NON', felicitations: 'NON', blame_travail: 'NON', blame_conduite: 'NON', observations: '' };
  const teacherName = bulletin.teacher_name || '';
  const isRepeating = student.is_repeating || false;

  const seqList = bulletin.sequences || [];
  const bType = bulletin.bulletin_type || 'annual';
  const bSeqId = bulletin.sequence_id;
  const bPeriodId = bulletin.evaluation_period_id;

  // ✅ Langue du bulletin : anglais si section anglophone
  const blang = getBulletinLang(bulletin, _lang);
  const L = getBulletinLabels(blang);
  const periodLabels = getPeriodLabels(bType, blang);

  let visibleSeqs: Array<{index: number; label: string; evaluation_period_id?: number}> = [];
  if (bType === 'sequence' && bSeqId) {
    const seq = seqList.find((s: any) => s.id === bSeqId);
    if (seq) {
      const idx = seqList.findIndex((s: any) => s.id === bSeqId);
      visibleSeqs = [{index: idx, label: seq.label || `Eval${idx + 1}`, evaluation_period_id: seq.evaluation_period_id}];
    }
  } else if (bType === 'trimester' && bPeriodId) {
    if (bulletin.is_competence_approach) {
      visibleSeqs = [{index: 0, label: L.note, evaluation_period_id: bPeriodId}];
    } else {
      visibleSeqs = seqList
        .map((s: any, i: number) => ({ ...s, index: i }))
        .filter((s: any) => s.evaluation_period_id === bPeriodId)
        .map((s: any) => ({ index: s.index, label: s.label || `Eval${s.index + 1}` }));
    }
  } else {
    visibleSeqs = seqList.map((s: any, i: number) => ({ index: i, label: s.label || `Eval${i + 1}`, evaluation_period_id: s.evaluation_period_id }));
  }

  const isCompetencyInSequence = (compTrimester: number, seqIndex: number) => {
    const seq = seqList[seqIndex];
    if (!seq) return false;
    const period = bulletin.periods?.find((p: any) => p.id === seq.evaluation_period_id);
    if (!period) return false;
    const periodNum = parseInt((period.code || '').replace(/[^0-9]/g, '')) || period.id;
    return periodNum === compTrimester;
  };

  const evals = bulletin.evaluations || [0,0,0,0,0,0];
  const trims = bulletin.trimesters || [0,0,0];

  const groupedSubjects = useMemo(() => {
    const groups: Record<string, BulletinSubject[]> = {};
    const noGroup: BulletinSubject[] = [];
    for (const subj of bulletin.subjects) {
      const g = subj.group_name;
      if (g && g.trim()) {
        if (!groups[g]) groups[g] = [];
        groups[g].push(subj);
      } else {
        noGroup.push(subj);
      }
    }
    return { groups, noGroup };
  }, [bulletin.subjects]);

  const isComp = !!bulletin.is_competence_approach;
  const totalCols = visibleSeqs.length + (isComp ? 12 : 10);

  const formatDate = (d: string | null) => {
    if (!d) return '-';
    try {
      const date = new Date(d);
      return date.toLocaleDateString(blang === 'en' ? 'en-GB' : 'fr-FR', { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric' });
    } catch { return d; }
  };

  // ═══════════════════════════════════════════════════════════════════
  // TABLEAU DE SYNTHÈSE — norme MINESEC :
  //   Rappel : séquence → uniquement la séquence courante + les précédentes
  //   TRAVAIL (ÉLÈVE | CLASSE) | CONDUITE | CONSEIL (DISTINCTIONS / SANCTIONS en cases à cocher)
  // ═══════════════════════════════════════════════════════════════════
  const workLabel = bType === 'sequence' ? L.workSeq : bType === 'trimester' ? L.workTri : L.workAnn;
  const conductLabel = bType === 'sequence' ? L.condSeq : bType === 'trimester' ? L.condTri : L.condAnn;
  const moyWord = blang === 'en' ? 'AVG.' : 'MOY.';
  const hourWord = blang === 'en' ? 'Hour(s)' : 'Heure(s)';
  const dayWord = blang === 'en' ? 'Day(s)' : 'Jour(s)';
  const classSize = aw.class_size || 0;
  const rangTxt = aw.rang_annuel
    ? (blang === 'fr' ? `${aw.rang_annuel}e` : `${aw.rang_annuel}`) + (classSize ? ` / ${classSize}` : '')
    : '-';
  // ✅ Distinctions / sanctions : cases à cocher (☒ coché, ☐ vide)
  const cb = (v: any) => (v === 'OUI' || v === 'YES') ? '☒' : '☐';

  // ✅ Rappel : séquentiel → séquence courante + précédentes uniquement
  let recapLabels: string[] = [];
  let recapValues: string[] = [];
  if (bType === 'sequence' && bSeqId) {
    const curIdx = seqList.findIndex((s: any) => s.id === bSeqId);
    const n = curIdx >= 0 ? curIdx + 1 : seqList.length;
    recapLabels = Array.from({ length: n }, (_, i) => L.evalAvg(i + 1));
    recapValues = evals.slice(0, n).map(v => v > 0 ? v.toFixed(2) : '-');
  } else if (bType === 'trimester' && bPeriodId) {
    const curIdx = (bulletin.periods || []).findIndex((p: any) => p.id === bPeriodId);
    const n = curIdx >= 0 ? curIdx + 1 : 3;
    recapLabels = Array.from({ length: n }, (_, i) => L.termAvg(i + 1));
    recapValues = trims.slice(0, n).map(v => v > 0 ? v.toFixed(2) : '-');
  } else {
    recapLabels = [1, 2, 3, 4, 5, 6].map(i => L.evalAvg(i)).concat([1, 2, 3].map(i => L.termAvg(i)));
    recapValues = [...evals, ...trims].map(v => v > 0 ? v.toFixed(2) : '-');
  }

  // 5 paires de colonnes : ÉLÈVE | CLASSE | CONDUITE | DISTINCTIONS | SANCTIONS
  const footerRows: Array<[string, string, string, string, string, string, string, string, string, string]> = [
    [L.totalPoints, aw.total_points?.toFixed(2) || '0.00', L.moyPremier, aw.moy_premier ? Number(aw.moy_premier).toFixed(2) : '-', L.absTotal, `${conduct.absence_hours || 0} ${hourWord}`, L.honorRoll, cb(council.tableau_honneur), L.avertWork, cb(council.avertissement_travail)],
    [L.totalCoef, aw.total_coef?.toFixed(2) || '0.00', L.moyDernier, aw.moy_dernier ? Number(aw.moy_dernier).toFixed(2) : '-', L.absUnjust, `${conduct.absence_unjustified || 0} ${hourWord}`, L.encouragements, cb(council.encouragements), L.avertConduct, cb(council.avertissement_conduite)],
    [`${moyWord} ${periodLabels.moyLabel}`, aw.moy_annuelle?.toFixed(2) || '0.00', L.classAvg, aw.moy_classe?.toFixed(2) || '0.00', L.exclusions, `${conduct.exclusion_days || 0} ${dayWord}`, L.congratulations, cb(council.felicitations), L.blameWork, cb(council.blame_travail)],
    [blang === 'en' ? 'RANK' : 'RANG', rangTxt, '', '', '', '', '', '', L.blameConduct, cb(council.blame_conduite)],
  ];

  const bulletinTitle = bType === 'sequence' ? L.titleSequence : bType === 'trimester' ? L.titleTrimester : L.titleAnnual;

  return (
    <div className="bg-white text-[10px] leading-tight max-w-[210mm] mx-auto">
      {/* HEADER — langue du bulletin à gauche, autre langue à droite */}
      <div className="text-center border-b-2 border-black pb-2 mb-2">
        <div className="flex justify-between items-start">
          <div className="text-left flex-1">
            <p className="text-[8px] font-bold">{L.repLeft ? header.republic_fr : header.republic_en}</p>
            <p className="text-[7px]">{L.repLeft ? header.motto_fr : header.motto_en}</p>
            <p className="text-[7px] mt-0.5">{header.region}</p>
            <p className="text-[7px]">{header.department}</p>
            <p className="text-[7px]">{header.delegation}</p>
            <p className="text-[7px]">{header.sub_division}</p>
          </div>
          <div className="mx-2">
            {header.logo_url ? (
              <img src={header.logo_url} alt="Logo" className="w-14 h-14 object-contain" />
            ) : (
              <School className="w-10 h-10 text-[#1e3a8a]" />
            )}
          </div>
          <div className="text-right flex-1">
            <p className="text-[8px] font-bold">{L.repLeft ? header.republic_en : header.republic_fr}</p>
            <p className="text-[7px]">{L.repLeft ? header.motto_en : header.motto_fr}</p>
            <p className="text-[7px] mt-0.5">{header.region?.replace('RÉGION', 'WEST REGION')}</p>
            <p className="text-[7px]">{header.department?.replace('DÉPARTEMENT', 'DIVISION')}</p>
            <p className="text-[7px]">{header.delegation?.replace('DÉLÉGATION', 'DELEGATION')}</p>
            <p className="text-[7px]">{header.sub_division?.replace('ARRONDISSEMENT', 'SUB DIVISION')}</p>
          </div>
        </div>
        <div className="mt-1">
          <h2 className="text-xs font-bold text-[#1e3a8a]">{header.school_name}</h2>
          <p className="text-[8px] text-gray-600">{header.school_name_en}</p>
          <p className="text-[7px] text-gray-500">{header.address} | {header.po_box}</p>
        </div>
        <div className="mt-1 border-t border-gray-300 pt-1">
          <h1 style={{fontSize:'14px', fontWeight:'bold', textTransform:'uppercase', letterSpacing:'1px'}}>
            {bulletinTitle}
          </h1>
          <p className="text-[10px] font-semibold">{ci.name || student.class_name}, {ci.level || ''}, {ci.section || ''}</p>
        </div>
      </div>

      {/* STUDENT INFO TABLE */}
      <div className="flex items-start gap-3 mb-2">
        <div className="w-20 h-24 rounded-lg overflow-hidden border border-gray-300 flex-shrink-0 bg-gray-100">
          {student.photo_url ? (
            <img src={resolvePhotoUrl(student.photo_url) || ''} alt={student.full_name}
              className="w-full h-full object-cover"
              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
            />
          ) : (
            <div className="w-full h-full bg-[#1e3a8a]/10 flex items-center justify-center text-xs font-bold text-[#1e3a8a]">
              {(student.first_name?.[0] || '') + (student.last_name?.[0] || '')}
            </div>
          )}
        </div>
        <table className="flex-1 border border-black border-collapse text-[9px]">
          <tbody>
            <tr>
              <td className="border border-black px-1 py-0.5 font-bold bg-gray-100 w-24">{L.names}</td>
              <td className="border border-black px-1 py-0.5 font-bold uppercase" colSpan={3}>{student.full_name}</td>
            </tr>
            <tr>
              <td className="border border-black px-1 py-0.5 font-bold bg-gray-100">{L.dob}</td>
              <td className="border border-black px-1 py-0.5">{formatDate(student.date_of_birth)}</td>
              <td className="border border-black px-1 py-0.5 font-bold bg-gray-100 w-20">{L.pob}</td>
              <td className="border border-black px-1 py-0.5">{student.place_of_birth || '-'}</td>
            </tr>
            <tr>
              <td className="border border-black px-1 py-0.5 font-bold bg-gray-100">{L.classLabel}</td>
              <td className="border border-black px-1 py-0.5">{student.class_name}</td>
              <td className="border border-black px-1 py-0.5 font-bold bg-gray-100">{L.matricule}</td>
              <td className="border border-black px-1 py-0.5">{student.matricule}</td>
            </tr>
            <tr>
              <td className="border border-black px-1 py-0.5 font-bold bg-gray-100">{L.classTeacher}</td>
              <td className="border border-black px-1 py-0.5">{teacherName}</td>
              <td className="border border-black px-1 py-0.5 font-bold bg-gray-100">{L.year}</td>
              <td className="border border-black px-1 py-0.5">{bulletin.academic_year_id || '2025-2026'}</td>
            </tr>
            <tr>
              <td className="border border-black px-1 py-0.5 font-bold bg-gray-100">{L.gender}</td>
              <td className="border border-black px-1 py-0.5">{student.gender === 'F' ? L.female : student.gender === 'M' ? L.male : '-'}</td>
              <td className="border border-black px-1 py-0.5 font-bold bg-gray-100">{L.situation}</td>
              <td className="border border-black px-1 py-0.5">{isRepeating ? L.repeater : L.newStudent}</td>
            </tr>
            <tr>
              <td className="border border-black px-1 py-0.5 font-bold bg-gray-100">{blang === 'en' ? 'REPEATER' : 'REDOUBLANT'}</td>
              <td className="border border-black px-1 py-0.5" colSpan={3}>{isRepeating ? L.yes : L.no}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* HISTORIQUE TRIMESTRES PRÉCÉDENTS */}
      {bulletin.previous_trimesters && bulletin.previous_trimesters.length > 0 && (
        <div className="border border-black p-1 mb-2 bg-gray-50">
          <p className="text-[8px] font-bold uppercase text-center mb-1">{L.prevTerms}</p>
          <div className="flex justify-around text-[8px]">
            {bulletin.previous_trimesters.map((pt, i) => (
              <div key={i} className="text-center">
                <span className="font-bold">{pt.period_label}</span>
                <br/>
                <span>{L.avgShort} {pt.average > 0 ? pt.average.toFixed(2) : '-'}</span>
                <br/>
                <span>{L.rankShort} {pt.rank > 0 ? pt.rank : '-'}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* SUBJECTS TABLE */}
      <table className="w-full border border-black border-collapse text-[8px]">
        <thead>
          <tr className="bg-gray-100">
            <th rowSpan={2} className="border border-black px-1 py-0.5 text-left font-bold">{L.subjects}</th>
            {visibleSeqs.map((s, i) => (
              <th key={i} rowSpan={2} className="border border-black px-1 py-0.5 text-center font-bold">{bulletin.is_competence_approach && bType === 'trimester' ? L.note : s.label}</th>
            ))}
            <th colSpan={4} className="border border-black px-1 py-0.5 text-center font-bold">
              {periodLabels.mainTitle}
            </th>
            {isComp ? (
              <th colSpan={3} className="border border-black px-1 py-0.5 text-center font-bold">{L.acquisition}</th>
            ) : (
              <th rowSpan={2} className="border border-black px-1 py-0.5 text-center font-bold">{blang === 'en' ? 'REMARK' : 'MENTION'}</th>
            )}
            <th colSpan={3} className="border border-black px-1 py-0.5 text-center font-bold">{L.classScores}</th>
            <th rowSpan={2} className="border border-black px-1 py-0.5 text-center font-bold">{L.teacherSig.split('\\n').map((x, i) => <React.Fragment key={i}>{i > 0 && <br/>}{x}</React.Fragment>)}</th>
          </tr>
          <tr className="bg-gray-100">
            <th className="border border-black px-1 py-0.5 text-center font-bold">{L.note}</th>
            <th className="border border-black px-1 py-0.5 text-center font-bold">{L.coef}</th>
            <th className="border border-black px-1 py-0.5 text-center font-bold">{L.nxC}</th>
            <th className="border border-black px-1 py-0.5 text-center font-bold">{L.rank}</th>
            {isComp && (
              <>
                <th className="border border-black px-1 py-0.5 text-center font-bold">{L.niveau}</th>
                <th className="border border-black px-1 py-0.5 text-center font-bold">{L.cote}</th>
                <th className="border border-black px-1 py-0.5 text-center font-bold">{L.apprec}</th>
              </>
            )}
            <th className="border border-black px-1 py-0.5 text-center font-bold">{L.min}</th>
            <th className="border border-black px-1 py-0.5 text-center font-bold">{L.moy}</th>
            <th className="border border-black px-1 py-0.5 text-center font-bold">{L.max}</th>
          </tr>
        </thead>
        <tbody>
          {groupedSubjects.noGroup.map((subj, idx) => {
            const hasComps = bulletin.is_competence_approach && subj.competencies && subj.competencies.length > 0;
            const baseBg = idx % 2 === 0 ? 'bg-white' : 'bg-gray-50';
            const hasNote = subj.display_note > 0;
            return (
              <React.Fragment key={subj.subject_id}>
                <tr className={baseBg}>
                  <td className="border border-black px-1 py-0.5 font-medium">{subj.subject_name}</td>
                  {visibleSeqs.map((vs) => (
                    <td key={vs.index} className="border border-black px-1 py-0.5 text-center">
                      {subj.sequence_scores[vs.index] > 0 ? subj.sequence_scores[vs.index].toFixed(2) : '-'}
                    </td>
                  ))}
                  <td className="border border-black px-1 py-0.5 text-center font-bold">
                    {subj.display_note > 0 ? subj.display_note.toFixed(2) : (subj.display_note === 0 ? '0.00' : '-')}
                  </td>
                  <td className="border border-black px-1 py-0.5 text-center">{subj.coefficient}</td>
                  <td className="border border-black px-1 py-0.5 text-center font-bold">
                    {subj.points > 0 ? subj.points.toFixed(2) : '-'}
                  </td>
                  <td className="border border-black px-1 py-0.5 text-center">{subj.rank > 0 ? subj.rank : '-'}</td>
                  {isComp ? (
                    <>
                      <td className="border border-black px-1 py-0.5 text-center font-bold">{hasNote ? getNiveauAcquisition(subj.display_note) : '-'}</td>
                      <td className="border border-black px-1 py-0.5 text-center font-bold">{hasNote ? getCote(subj.display_note) : '-'}</td>
                      <td className="border border-black px-1 py-0.5 text-center font-bold">
                        {hasNote ? getAppreciation(subj.display_note, true, blang) : '-'}
                      </td>
                    </>
                  ) : (
                    <td className="border border-black px-1 py-0.5 text-center font-bold">
                      {blang === 'fr' ? subj.mention : getAppreciation(subj.display_note, false, 'en')}
                    </td>
                  )}
                  <td className="border border-black px-1 py-0.5 text-center text-gray-500">
                    {subj.class_stats?.min > 0 ? subj.class_stats.min.toFixed(2) : '-'}
                  </td>
                  <td className="border border-black px-1 py-0.5 text-center text-gray-500">
                    {subj.class_stats?.avg > 0 ? subj.class_stats.avg.toFixed(2) : '-'}
                  </td>
                  <td className="border border-black px-1 py-0.5 text-center text-gray-500">
                    {subj.class_stats?.max > 0 ? subj.class_stats.max.toFixed(2) : '-'}
                  </td>
                  <td className="border border-black px-1 py-0.5 text-center text-[10px]">{subj.teacher_name || '-'}</td>
                </tr>
                {hasComps && subj.competencies!.map((comp, cidx) => (
                  <tr key={`comp-${cidx}`} className={baseBg}>
                    <td className="border border-black px-1 py-0.5 pl-4 text-[8px] text-gray-700">
                      └─ T{comp.trimester} C{comp.number} : {comp.label_fr}
                    </td>
                    {visibleSeqs.map((vs, vidx) => (
                      <td key={vidx} className="border border-black px-1 py-0.5 text-center text-[8px]">
                        {isCompetencyInSequence(comp.trimester, vs.index) && comp.score != null ? comp.score.toFixed(2) : '-'}
                      </td>
                    ))}
                    <td className="border border-black px-1 py-0.5 text-center text-[8px]">-</td>
                    <td className="border border-black px-1 py-0.5 text-center text-[8px]">-</td>
                    <td className="border border-black px-1 py-0.5 text-center text-[8px]">-</td>
                    <td className="border border-black px-1 py-0.5 text-center text-[8px]">-</td>
                    {isComp ? (
                      <>
                        <td className="border border-black px-1 py-0.5 text-center text-[8px] font-bold">
                          {comp.score != null ? getNiveauAcquisition(comp.score) : '-'}
                        </td>
                        <td className="border border-black px-1 py-0.5 text-center text-[8px] font-bold">
                          {comp.score != null ? getCote(comp.score) : '-'}
                        </td>
                        <td className="border border-black px-1 py-0.5 text-center text-[8px]">{comp.appreciation}</td>
                      </>
                    ) : (
                      <td className="border border-black px-1 py-0.5 text-center text-[9px]">{comp.appreciation}</td>
                    )}
                    <td className="border border-black px-1 py-0.5 text-center text-[8px]">-</td>
                    <td className="border border-black px-1 py-0.5 text-center text-[8px]">-</td>
                    <td className="border border-black px-1 py-0.5 text-center text-[8px]">-</td>
                    <td className="border border-black px-1 py-0.5 text-center text-[8px]">-</td>
                  </tr>
                ))}
              </React.Fragment>
            );
          })}
          {(() => {
            const scoredSubjects = groupedSubjects.noGroup.filter(subj => (subj.points || 0) > 0);
            const totalCoef = scoredSubjects.reduce((s, subj) => s + (subj.coefficient || 0), 0);
            const totalPts = scoredSubjects.reduce((s, subj) => s + (subj.points || 0), 0);
            const avg = totalCoef > 0 ? totalPts / totalCoef : 0;
            return groupedSubjects.noGroup.length > 0 && (
              <tr className="bg-gray-100 font-bold">
                <td className="border border-black px-1 py-0.5">{L.total}</td>
                {visibleSeqs.map((vs) => (
                  <td key={vs.index} className="border border-black px-1 py-0.5 text-center">-</td>
                ))}
                <td className="border border-black px-1 py-0.5 text-center">{avg > 0 ? avg.toFixed(2) : '-'}</td>
                <td className="border border-black px-1 py-0.5 text-center">{totalCoef}</td>
                <td className="border border-black px-1 py-0.5 text-center">{totalPts > 0 ? totalPts.toFixed(2) : '-'}</td>
                <td className="border border-black px-1 py-0.5 text-center">-</td>
                {isComp ? (
                  <>
                    <td className="border border-black px-1 py-0.5 text-center">-</td>
                    <td className="border border-black px-1 py-0.5 text-center">-</td>
                    <td className="border border-black px-1 py-0.5 text-center">{avg > 0 ? getAppreciation(avg, true, blang) : '-'}</td>
                  </>
                ) : (
                  <td className="border border-black px-1 py-0.5 text-center">{avg > 0 ? getAppreciation(avg, false, blang) : '-'}</td>
                )}
                <td className="border border-black px-1 py-0.5 text-center">-</td>
                <td className="border border-black px-1 py-0.5 text-center">-</td>
                <td className="border border-black px-1 py-0.5 text-center">-</td>
                <td className="border border-black px-1 py-0.5 text-center">-</td>
              </tr>
            );
          })()}
          {Object.entries(groupedSubjects.groups).map(([groupName, subjects]) => {
            const scoredSubjects = subjects.filter(subj => (subj.points || 0) > 0);
            const totalCoef = scoredSubjects.reduce((s, subj) => s + (subj.coefficient || 0), 0);
            const totalPts = scoredSubjects.reduce((s, subj) => s + (subj.points || 0), 0);
            const avg = totalCoef > 0 ? totalPts / totalCoef : 0;
            return (
              <React.Fragment key={groupName}>
                <tr className="bg-gray-200">
                  <td colSpan={totalCols} className="border border-black px-1 py-0.5 font-bold text-center uppercase">
                    {groupName}
                  </td>
                </tr>
                {subjects.map((subj, idx) => {
                  const hasComps = bulletin.is_competence_approach && subj.competencies && subj.competencies.length > 0;
                  const baseBg = idx % 2 === 0 ? 'bg-white' : 'bg-gray-50';
                  const hasNote = subj.display_note > 0;
                  return (
                    <React.Fragment key={subj.subject_id}>
                      <tr className={baseBg}>
                        <td className="border border-black px-1 py-0.5 font-medium pl-3">{subj.subject_name}</td>
                        {visibleSeqs.map((vs) => (
                          <td key={vs.index} className="border border-black px-1 py-0.5 text-center">
                            {subj.sequence_scores[vs.index] > 0 ? subj.sequence_scores[vs.index].toFixed(2) : '-'}
                          </td>
                        ))}
                        <td className="border border-black px-1 py-0.5 text-center font-bold">{subj.display_note > 0 ? subj.display_note.toFixed(2) : '-'}</td>
                        <td className="border border-black px-1 py-0.5 text-center">{subj.coefficient}</td>
                        <td className="border border-black px-1 py-0.5 text-center font-bold">{subj.points > 0 ? subj.points.toFixed(2) : '-'}</td>
                        <td className="border border-black px-1 py-0.5 text-center">{subj.rank > 0 ? subj.rank : '-'}</td>
                        {isComp ? (
                          <>
                            <td className="border border-black px-1 py-0.5 text-center font-bold">{hasNote ? getNiveauAcquisition(subj.display_note) : '-'}</td>
                            <td className="border border-black px-1 py-0.5 text-center font-bold">{hasNote ? getCote(subj.display_note) : '-'}</td>
                            <td className="border border-black px-1 py-0.5 text-center font-bold">
                              {hasNote ? getAppreciation(subj.display_note, true, blang) : '-'}
                            </td>
                          </>
                        ) : (
                          <td className="border border-black px-1 py-0.5 text-center font-bold">
                            {blang === 'fr' ? subj.mention : getAppreciation(subj.display_note, false, 'en')}
                          </td>
                        )}
                        <td className="border border-black px-1 py-0.5 text-center text-gray-500">{subj.class_stats?.min > 0 ? subj.class_stats.min.toFixed(2) : '-'}</td>
                        <td className="border border-black px-1 py-0.5 text-center text-gray-500">{subj.class_stats?.avg > 0 ? subj.class_stats.avg.toFixed(2) : '-'}</td>
                        <td className="border border-black px-1 py-0.5 text-center text-gray-500">{subj.class_stats?.max > 0 ? subj.class_stats.max.toFixed(2) : '-'}</td>
                        <td className="border border-black px-1 py-0.5 text-center text-[10px]">{subj.teacher_name || '-'}</td>
                      </tr>
                      {hasComps && subj.competencies!.map((comp, cidx) => (
                        <tr key={`comp-${cidx}`} className={baseBg}>
                          <td className="border border-black px-1 py-0.5 pl-6 text-[8px] text-gray-700">
                            └─ T{comp.trimester} C{comp.number} : {comp.label_fr}
                          </td>
                          {visibleSeqs.map((vs, vidx) => (
                            <td key={vidx} className="border border-black px-1 py-0.5 text-center text-[8px]">
                              {isCompetencyInSequence(comp.trimester, vs.index) && comp.score != null ? comp.score.toFixed(2) : '-'}
                            </td>
                          ))}
                          <td className="border border-black px-1 py-0.5 text-center font-bold text-[9px]">
                            {comp.score > 0 ? comp.score.toFixed(2) : '-'}
                          </td>
                          <td className="border border-black px-1 py-0.5 text-center text-[8px]">-</td>
                          <td className="border border-black px-1 py-0.5 text-center text-[8px]">-</td>
                          <td className="border border-black px-1 py-0.5 text-center text-[8px]">-</td>
                          {isComp ? (
                            <>
                              <td className="border border-black px-1 py-0.5 text-center text-[8px] font-bold">
                                {comp.score != null ? getNiveauAcquisition(comp.score) : '-'}
                              </td>
                              <td className="border border-black px-1 py-0.5 text-center text-[8px] font-bold">
                                {comp.score != null ? getCote(comp.score) : '-'}
                              </td>
                              <td className="border border-black px-1 py-0.5 text-center text-[8px]">{comp.appreciation}</td>
                            </>
                          ) : (
                            <td className="border border-black px-1 py-0.5 text-center text-[9px]">{comp.appreciation}</td>
                          )}
                          <td className="border border-black px-1 py-0.5 text-center text-[8px]">-</td>
                          <td className="border border-black px-1 py-0.5 text-center text-[8px]">-</td>
                          <td className="border border-black px-1 py-0.5 text-center text-[8px]">-</td>
                          <td className="border border-black px-1 py-0.5 text-center text-[8px]">-</td>
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })}
                <tr className="bg-gray-100 font-bold">
                  <td className="border border-black px-1 py-0.5">{L.total} {groupName}</td>
                  {visibleSeqs.map((vs) => (
                    <td key={vs.index} className="border border-black px-1 py-0.5 text-center">-</td>
                  ))}
                  <td className="border border-black px-1 py-0.5 text-center">{avg > 0 ? avg.toFixed(2) : '-'}</td>
                  <td className="border border-black px-1 py-0.5 text-center">{totalCoef}</td>
                  <td className="border border-black px-1 py-0.5 text-center">{totalPts > 0 ? totalPts.toFixed(2) : '-'}</td>
                  <td className="border border-black px-1 py-0.5 text-center">-</td>
                  {isComp ? (
                    <>
                      <td className="border border-black px-1 py-0.5 text-center">-</td>
                      <td className="border border-black px-1 py-0.5 text-center">-</td>
                      <td className="border border-black px-1 py-0.5 text-center">{avg > 0 ? getAppreciation(avg, true, blang) : '-'}</td>
                    </>
                  ) : (
                    <td className="border border-black px-1 py-0.5 text-center">{avg > 0 ? getAppreciation(avg, false, blang) : '-'}</td>
                  )}
                  <td className="border border-black px-1 py-0.5 text-center">-</td>
                  <td className="border border-black px-1 py-0.5 text-center">-</td>
                  <td className="border border-black px-1 py-0.5 text-center">-</td>
                  <td className="border border-black px-1 py-0.5 text-center">-</td>
                </tr>
              </React.Fragment>
            );
          })}
        </tbody>
      </table>

      {/* RAPPEL : séquence → courante + précédentes | trimestre → courant + précédents | annuel → tout */}
      <table className="w-full border border-black border-collapse text-[8px] mt-1">
        <thead>
          <tr className="bg-gray-100">
            {recapLabels.map((lbl, i) => (
              <th key={i} className="border border-black px-1 py-0.5 text-center font-bold" style={{width: `${100 / recapLabels.length}%`}}>{lbl}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            {recapValues.map((v, i) => (
              <td key={i} className="border border-black px-1 py-0.5 text-center font-bold">{v}</td>
            ))}
          </tr>
        </tbody>
      </table>

      {/* TABLEAU DE SYNTHÈSE MINESEC : TRAVAIL (ÉLÈVE | CLASSE) | CONDUITE | CONSEIL (DISTINCTIONS | SANCTIONS ☒/☐) */}
      <table className="w-full border border-black border-collapse text-[8px] mt-1">
        <thead>
          <tr className="bg-gray-100">
            <th colSpan={4} className="border border-black px-1 py-0.5 text-center font-bold">{workLabel}</th>
            <th colSpan={2} className="border border-black px-1 py-0.5 text-center font-bold">{conductLabel}</th>
            <th colSpan={4} className="border border-black px-1 py-0.5 text-center font-bold">{L.council}</th>
          </tr>
          <tr className="bg-gray-100">
            <th colSpan={2} className="border border-black px-1 py-0.5 text-center font-bold">{L.eleve}</th>
            <th colSpan={2} className="border border-black px-1 py-0.5 text-center font-bold">{L.classe}</th>
            <th colSpan={2} className="border border-black px-0.5 py-0.5"></th>
            <th colSpan={2} className="border border-black px-1 py-0.5 text-center font-bold">{L.distinctions}</th>
            <th colSpan={2} className="border border-black px-1 py-0.5 text-center font-bold">{L.sanctions}</th>
          </tr>
        </thead>
        <tbody>
          {footerRows.map((row, idx) => (
            <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
              <td className="border border-black px-1 py-0.5 font-medium">{row[0]}</td>
              <td className="border border-black px-1 py-0.5 text-right font-bold">{row[1]}</td>
              <td className="border border-black px-1 py-0.5 font-medium">{row[2]}</td>
              <td className="border border-black px-1 py-0.5 text-right font-bold">{row[3]}</td>
              <td className="border border-black px-1 py-0.5 font-medium">{row[4]}</td>
              <td className="border border-black px-1 py-0.5 text-right font-bold">{row[5]}</td>
              <td className="border border-black px-1 py-0.5 font-medium">{row[6]}</td>
              <td className="border border-black px-1 py-0.5 text-center font-bold">{row[7]}</td>
              <td className="border border-black px-1 py-0.5 font-medium">{row[8]}</td>
              <td className="border border-black px-1 py-0.5 text-center font-bold">{row[9]}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* OBSERVATIONS + VISA */}
      <div className="grid grid-cols-2 gap-0 border border-black border-t-0 text-[8px]">
        <div className="border-r border-black p-2 min-h-[80px]">
          <h4 className="font-bold text-center bg-gray-100 border-b border-black -mx-2 -mt-2 px-2 py-0.5 mb-1">{L.obsTitle}</h4>
          {bulletin.weak_subjects && bulletin.weak_subjects.length > 0 && (
            <p className="text-[8px] font-bold text-red-700 mt-1">
              {L.efforts} {bulletin.weak_subjects.map(w => `${w.subject_name} (${w.note.toFixed(2)})`).join(', ')}
            </p>
          )}
          <p className="italic text-gray-600 mt-1 whitespace-pre-line">{council.observations || ''}</p>
        </div>
        <div className="p-2 min-h-[80px]">
          <h4 className="font-bold text-center bg-gray-100 border-b border-black -mx-2 -mt-2 px-2 py-0.5 mb-1">{L.visa}</h4>
          <div className="mt-8 border-t border-gray-400 w-32 mx-auto" />
        </div>
      </div>

      {/* SIGNATURES : parents (pas le titulaire) + chef d'établissement */}
      <div className="flex justify-between mt-4 text-[8px]">
        <div className="text-center w-1/3">
          <p className="font-bold">{L.parentsSig}</p>
          <div className="border-t border-gray-400 w-24 mx-auto mt-8" />
        </div>
        <div className="text-center w-1/3">
          <p className="font-bold">{L.principal}</p>
          <div className="border-t border-gray-400 w-24 mx-auto mt-8" />
        </div>
      </div>
    </div>
  );
}

function generateBulletinHTML(bulletin: BulletinData, header: SchoolHeader, _lang: string): string {
  const student = bulletin.student;
  const ci = bulletin.class_info || { name: '', level: '', level_code: '', cycle: '', section: '' };
  const aw = bulletin.annual_work || { total_points: 0, total_coef: 0, moy_annuelle: 0, rang_annuel: 0, nbre_matieres: '-', moy_classe: 0 };
  const conduct = bulletin.conduct || { absence_hours: 0, absence_unjustified: 0, detention_days: 0, exclusion_days: 0, note: 20 };
  const council = bulletin.council || { tableau_honneur: 'NON', encouragements: 'NON', felicitations: 'NON', blame_travail: 'NON', blame_conduite: 'NON', observations: '' };
  const teacherName = bulletin.teacher_name || '';
  const isRepeating = student.is_repeating || false;
  const evals = bulletin.evaluations || [0, 0, 0, 0, 0, 0];
  const trims = bulletin.trimesters || [0, 0, 0];

  const seqList = bulletin.sequences || [];
  const bType = bulletin.bulletin_type || 'annual';

  // ✅ Langue du bulletin : anglais si section anglophone
  const blang = getBulletinLang(bulletin, _lang);
  const L = getBulletinLabels(blang);
  const periodLabels = getPeriodLabels(bType, blang);

  const isCompetencyInSequenceHTML = (compTrimester: number, seqIndex: number) => {
    const seq = seqList[seqIndex];
    if (!seq) return false;
    const period = bulletin.periods?.find((p: any) => p.id === seq.evaluation_period_id);
    if (!period) return false;
    const periodNum = parseInt((period.code || '').replace(/[^0-9]/g, '')) || period.id;
    return periodNum === compTrimester;
  };

  const bSeqId = bulletin.sequence_id;
  const bPeriodId = bulletin.evaluation_period_id;

  let visibleSeqs: Array<{index: number; label: string; evaluation_period_id?: number}> = [];
  if (bType === 'sequence' && bSeqId) {
    const seq = seqList.find((s: any) => s.id === bSeqId);
    if (seq) {
      const idx = seqList.findIndex((s: any) => s.id === bSeqId);
      visibleSeqs = [{index: idx, label: seq.label || `Eval${idx + 1}`, evaluation_period_id: seq.evaluation_period_id}];
    }
  } else if (bType === 'trimester' && bPeriodId) {
    if (bulletin.is_competence_approach) {
      visibleSeqs = [{index: 0, label: L.note, evaluation_period_id: bPeriodId}];
    } else {
      visibleSeqs = seqList
        .map((s: any, i: number) => ({ ...s, index: i }))
        .filter((s: any) => s.evaluation_period_id === bPeriodId)
        .map((s: any) => ({ index: s.index, label: s.label || `Eval${s.index + 1}` }));
    }
  } else {
    visibleSeqs = seqList.map((s: any, i: number) => ({ index: i, label: s.label || `Eval${i + 1}`, evaluation_period_id: s.evaluation_period_id }));
  }

  const isComp = !!bulletin.is_competence_approach;
  const totalCols = visibleSeqs.length + (isComp ? 12 : 10);

  const formatDate = (d: string | null) => {
    if (!d) return '-';
    try {
      const date = new Date(d);
      return date.toLocaleDateString(blang === 'en' ? 'en-GB' : 'fr-FR', { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric' });
    } catch { return d; }
  };

  const groups: Record<string, typeof bulletin.subjects> = {};
  const noGroup: typeof bulletin.subjects = [];
  for (const subj of bulletin.subjects) {
    const g = subj.group_name;
    if (g && g.trim()) {
      if (!groups[g]) groups[g] = [];
      groups[g].push(subj);
    } else {
      noGroup.push(subj);
    }
  }

  const hourWord = blang === 'en' ? 'Hour(s)' : 'Heure(s)';
  const dayWord = blang === 'en' ? 'Day(s)' : 'Jour(s)';

  const acquisitionCells = (score: number, hasScore: boolean): string => {
    if (!isComp) {
      return '';
    }
    return `
      <td style="border:1px solid black;padding:2px 4px;text-align:center;font-weight:bold;">${hasScore ? getNiveauAcquisition(score) : '-'}</td>
      <td style="border:1px solid black;padding:2px 4px;text-align:center;font-weight:bold;">${hasScore ? getCote(score) : '-'}</td>
      <td style="border:1px solid black;padding:2px 4px;text-align:center;">${hasScore ? getAppreciation(score, true, blang) : '-'}</td>`;
  };

  const mentionCell = (subj: BulletinSubject): string => {
    const txt = blang === 'fr' ? (subj.mention || '') : getAppreciation(subj.display_note, false, 'en');
    return `<td style="border:1px solid #000;padding:2px 4px;text-align:center;font-weight:bold;">${txt}</td>`;
  };

  const emptyAcquisitionCells = (): string => {
    if (!isComp) return '';
    return `
      <td style="border:1px solid black;padding:2px 4px;text-align:center;font-size:8px;">-</td>
      <td style="border:1px solid black;padding:2px 4px;text-align:center;font-size:8px;">-</td>
      <td style="border:1px solid black;padding:2px 4px;text-align:center;font-size:8px;">-</td>`;
  };

  const compAcquisitionCells = (comp: any): string => {
    if (!isComp) return `<td style="border:1px solid black;padding:2px 4px;text-align:center;font-size:8px;">${comp.appreciation}</td>`;
    return `
      <td style="border:1px solid black;padding:2px 4px;text-align:center;font-size:8px;font-weight:bold;">${comp.score != null ? getNiveauAcquisition(comp.score) : '-'}</td>
      <td style="border:1px solid black;padding:2px 4px;text-align:center;font-size:8px;font-weight:bold;">${comp.score != null ? getCote(comp.score) : '-'}</td>
      <td style="border:1px solid black;padding:2px 4px;text-align:center;font-size:8px;">${comp.appreciation}</td>`;
  };

  const totalAcquisitionCells = (avg: number): string => {
    if (!isComp) return `<td style="border:1px solid black;padding:2px 4px;text-align:center;">${avg > 0 ? getAppreciation(avg, false, blang) : '-'}</td>`;
    return `
      <td style="border:1px solid black;padding:2px 4px;text-align:center;">-</td>
      <td style="border:1px solid black;padding:2px 4px;text-align:center;">-</td>
      <td style="border:1px solid black;padding:2px 4px;text-align:center;">${avg > 0 ? getAppreciation(avg, true, blang) : '-'}</td>`;
  };

  // Lignes du tableau des matières
  let subjectsRows = '';
  for (const subj of noGroup) {
    const hasComps = bulletin.is_competence_approach && subj.competencies && subj.competencies.length > 0;
    const compAvg = subj.display_note;
    const hasNote = subj.display_note > 0;
    const evalCells = visibleSeqs.map((vs) => {
      const score = subj.sequence_scores[vs.index] || 0;
      return `<td style="border:1px solid black;padding:2px 4px;text-align:center;font-size:8px;">${score > 0 ? score.toFixed(2) : '-'}</td>`;
    }).join('');
    subjectsRows += `
      <tr style="background:white;font-size:8px;">
        <td style="border:1px solid black;padding:2px 4px;font-weight:600;">${subj.subject_name}</td>
        ${evalCells}
        <td style="border:1px solid black;padding:2px 4px;text-align:center;font-weight:bold;">${compAvg > 0 ? compAvg.toFixed(2) : (compAvg === 0 ? '0.00' : '-')}</td>
        <td style="border:1px solid black;padding:2px 4px;text-align:center;">${subj.coefficient}</td>
        <td style="border:1px solid black;padding:2px 4px;text-align:center;font-weight:bold;">${subj.points > 0 ? subj.points.toFixed(2) : '-'}</td>
        <td style="border:1px solid black;padding:2px 4px;text-align:center;">${subj.rank > 0 ? subj.rank : '-'}</td>
        ${isComp ? acquisitionCells(subj.display_note, hasNote) : mentionCell(subj)}
        <td style="border:1px solid black;padding:2px 4px;text-align:center;color:#666;">${subj.class_stats?.min > 0 ? subj.class_stats.min.toFixed(2) : '-'}</td>
        <td style="border:1px solid black;padding:2px 4px;text-align:center;color:#666;">${subj.class_stats?.avg > 0 ? subj.class_stats.avg.toFixed(2) : '-'}</td>
        <td style="border:1px solid black;padding:2px 4px;text-align:center;color:#666;">${subj.class_stats?.max > 0 ? subj.class_stats.max.toFixed(2) : '-'}</td>
        <td style="border:1px solid black;padding:2px 4px;text-align:center;font-size:7px;">${subj.teacher_name || '-'}</td>
      </tr>
    `;
    if (hasComps) {
      for (const comp of subj.competencies!) {
        const seqCells = visibleSeqs.map((vs) => {
          const match = isCompetencyInSequenceHTML(comp.trimester, vs.index);
          return `<td style="border:1px solid black;padding:2px 4px;text-align:center;font-size:8px;">${match && comp.score != null ? comp.score.toFixed(2) : '-'}</td>`;
        }).join('');
        subjectsRows += `
          <tr style="background:white;font-size:8px;">
            <td style="border:1px solid black;padding:2px 4px;padding-left:16px;font-size:7px;color:#555;">
              └─ T${comp.trimester} C${comp.number} : ${comp.label_fr}
            </td>
            ${seqCells}
            <td style="border:1px solid black;padding:2px 4px;text-align:center;font-size:7px;">-</td>
            <td style="border:1px solid black;padding:2px 4px;text-align:center;font-size:7px;">-</td>
            <td style="border:1px solid black;padding:2px 4px;text-align:center;font-size:7px;">-</td>
            <td style="border:1px solid black;padding:2px 4px;text-align:center;font-size:7px;">-</td>
            ${compAcquisitionCells(comp)}
            <td style="border:1px solid black;padding:2px 4px;text-align:center;font-size:7px;">-</td>
            <td style="border:1px solid black;padding:2px 4px;text-align:center;font-size:7px;">-</td>
            <td style="border:1px solid black;padding:2px 4px;text-align:center;font-size:7px;">-</td>
            <td style="border:1px solid black;padding:2px 4px;text-align:center;font-size:7px;">-</td>
          </tr>
        `;
      }
    }
  }
  // Total matières sans groupe
  if (noGroup.length > 0) {
    const scoredNoGroup = noGroup.filter(subj => (subj.points || 0) > 0);
    const totalCoef = scoredNoGroup.reduce((s, subj) => s + (subj.coefficient || 0), 0);
    const totalPts = scoredNoGroup.reduce((s, subj) => s + (subj.points || 0), 0);
    const avg = totalCoef > 0 ? totalPts / totalCoef : 0;
    const emptyCells = visibleSeqs.map(() => '<td style="border:1px solid black;padding:2px 4px;text-align:center;font-size:8px;">-</td>').join('');
    subjectsRows += `
      <tr style="background:#f3f4f6;font-size:8px;font-weight:bold;">
        <td style="border:1px solid black;padding:2px 4px;font-weight:600;">${L.total}</td>
        ${emptyCells}
        <td style="border:1px solid black;padding:2px 4px;text-align:center;">${avg > 0 ? avg.toFixed(2) : '-'}</td>
        <td style="border:1px solid black;padding:2px 4px;text-align:center;">${totalCoef}</td>
        <td style="border:1px solid black;padding:2px 4px;text-align:center;">${totalPts > 0 ? totalPts.toFixed(2) : '-'}</td>
        <td style="border:1px solid black;padding:2px 4px;text-align:center;">-</td>
        ${totalAcquisitionCells(avg)}
        <td style="border:1px solid black;padding:2px 4px;text-align:center;">-</td>
        <td style="border:1px solid black;padding:2px 4px;text-align:center;">-</td>
        <td style="border:1px solid black;padding:2px 4px;text-align:center;">-</td>
        <td style="border:1px solid black;padding:2px 4px;text-align:center;">-</td>
      </tr>
    `;
  }
  for (const [groupName, subjects] of Object.entries(groups)) {
    const scoredGroup = subjects.filter(subj => (subj.points || 0) > 0);
    const totalCoef = scoredGroup.reduce((s, subj) => s + (subj.coefficient || 0), 0);
    const totalPts = scoredGroup.reduce((s, subj) => s + (subj.points || 0), 0);
    const avg = totalCoef > 0 ? totalPts / totalCoef : 0;
    subjectsRows += `
      <tr style="background:#e5e7eb;font-size:8px;">
        <td colspan="${totalCols}" style="border:1px solid black;padding:2px 4px;font-weight:bold;text-align:center;text-transform:uppercase;">${groupName}</td>
      </tr>
    `;
    for (const subj of subjects) {
      const hasComps = bulletin.is_competence_approach && subj.competencies && subj.competencies.length > 0;
      const compAvgG = subj.display_note;
      const hasNote = subj.display_note > 0;
      const evalCells = visibleSeqs.map((vs) => {
        const score = subj.sequence_scores[vs.index] || 0;
        return `<td style="border:1px solid black;padding:2px 4px;text-align:center;font-size:8px;">${score > 0 ? score.toFixed(2) : '-'}</td>`;
      }).join('');
      subjectsRows += `
        <tr style="background:white;font-size:8px;">
          <td style="border:1px solid black;padding:2px 4px;font-weight:600;padding-left:12px;">${subj.subject_name}</td>
          ${evalCells}
          <td style="border:1px solid black;padding:2px 4px;text-align:center;font-weight:bold;">${compAvgG > 0 ? compAvgG.toFixed(2) : (compAvgG === 0 ? '0.00' : '-')}</td>
          <td style="border:1px solid black;padding:2px 4px;text-align:center;">${subj.coefficient}</td>
          <td style="border:1px solid black;padding:2px 4px;text-align:center;font-weight:bold;">${subj.points > 0 ? subj.points.toFixed(2) : '-'}</td>
          <td style="border:1px solid black;padding:2px 4px;text-align:center;">${subj.rank > 0 ? subj.rank : '-'}</td>
          ${isComp ? acquisitionCells(subj.display_note, hasNote) : mentionCell(subj)}
          <td style="border:1px solid black;padding:2px 4px;text-align:center;color:#666;">${subj.class_stats?.min > 0 ? subj.class_stats.min.toFixed(2) : '-'}</td>
          <td style="border:1px solid black;padding:2px 4px;text-align:center;color:#666;">${subj.class_stats?.avg > 0 ? subj.class_stats.avg.toFixed(2) : '-'}</td>
          <td style="border:1px solid black;padding:2px 4px;text-align:center;color:#666;">${subj.class_stats?.max > 0 ? subj.class_stats.max.toFixed(2) : '-'}</td>
          <td style="border:1px solid black;padding:2px 4px;text-align:center;font-size:7px;">${subj.teacher_name || '-'}</td>
        </tr>
      `;
      if (hasComps) {
        for (const comp of subj.competencies!) {
          const seqCells = visibleSeqs.map((vs) => {
            const match = isCompetencyInSequenceHTML(comp.trimester, vs.index);
            return `<td style="border:1px solid black;padding:2px 4px;text-align:center;font-size:8px;">${match && comp.score != null ? comp.score.toFixed(2) : '-'}</td>`;
          }).join('');
          subjectsRows += `
            <tr style="background:white;font-size:8px;">
              <td style="border:1px solid black;padding:2px 4px;padding-left:24px;font-size:7px;color:#555;">
                └─ T${comp.trimester} C${comp.number} : ${comp.label_fr}
              </td>
              ${seqCells}
              <td style="border:1px solid black;padding:2px 4px;text-align:center;font-size:7px;">-</td>
              <td style="border:1px solid black;padding:2px 4px;text-align:center;font-size:7px;">-</td>
              <td style="border:1px solid black;padding:2px 4px;text-align:center;font-size:7px;">-</td>
              <td style="border:1px solid black;padding:2px 4px;text-align:center;font-size:7px;">-</td>
              ${compAcquisitionCells(comp)}
              <td style="border:1px solid black;padding:2px 4px;text-align:center;font-size:7px;">-</td>
              <td style="border:1px solid black;padding:2px 4px;text-align:center;font-size:7px;">-</td>
              <td style="border:1px solid black;padding:2px 4px;text-align:center;font-size:7px;">-</td>
              <td style="border:1px solid black;padding:2px 4px;text-align:center;font-size:7px;">-</td>
            </tr>
          `;
        }
      }
    }
    const emptyCells = visibleSeqs.map(() => '<td style="border:1px solid black;padding:2px 4px;text-align:center;font-size:8px;">-</td>').join('');
    subjectsRows += `
      <tr style="background:#f3f4f6;font-size:8px;font-weight:bold;">
        <td style="border:1px solid black;padding:2px 4px;font-weight:600;">${L.total} ${groupName}</td>
        ${emptyCells}
        <td style="border:1px solid black;padding:2px 4px;text-align:center;">${avg > 0 ? avg.toFixed(2) : '-'}</td>
        <td style="border:1px solid black;padding:2px 4px;text-align:center;">${totalCoef}</td>
        <td style="border:1px solid black;padding:2px 4px;text-align:center;">${totalPts > 0 ? totalPts.toFixed(2) : '-'}</td>
        <td style="border:1px solid black;padding:2px 4px;text-align:center;">-</td>
        ${totalAcquisitionCells(avg)}
        <td style="border:1px solid black;padding:2px 4px;text-align:center;">-</td>
        <td style="border:1px solid black;padding:2px 4px;text-align:center;">-</td>
        <td style="border:1px solid black;padding:2px 4px;text-align:center;">-</td>
        <td style="border:1px solid black;padding:2px 4px;text-align:center;">-</td>
      </tr>
    `;
  }

  const seqHeadersHTML = visibleSeqs.map((s) =>
    `<th rowspan="2" style="border:1px solid black;padding:2px 4px;text-align:center;font-weight:bold;">${bulletin.is_competence_approach && bType === 'trimester' ? L.note : s.label}</th>`
  ).join('');

  const acquisitionHeaderHTML = isComp
    ? `<th colspan="3" style="border:1px solid black;padding:2px 4px;text-align:center;font-weight:bold;">${L.acquisition}</th>`
    : `<th rowspan="2" style="border:1px solid black;padding:2px 4px;text-align:center;font-weight:bold;">${blang === 'en' ? 'REMARK' : 'MENTION'}</th>`;
  const acquisitionSubHeaderHTML = isComp
    ? `<th style="border:1px solid black;padding:2px 4px;text-align:center;font-weight:bold;">${L.niveau}</th>
       <th style="border:1px solid black;padding:2px 4px;text-align:center;font-weight:bold;">${L.cote}</th>
       <th style="border:1px solid black;padding:2px 4px;text-align:center;font-weight:bold;">${L.apprec}</th>`
    : '';

  const workLabel = bType === 'sequence' ? L.workSeq : bType === 'trimester' ? L.workTri : L.workAnn;
  const conductLabel = bType === 'sequence' ? L.condSeq : bType === 'trimester' ? L.condTri : L.condAnn;
  const moyWord = blang === 'en' ? 'AVG.' : 'MOY.';
  const classSize = aw.class_size || 0;
  const rangTxt = aw.rang_annuel
    ? (blang === 'fr' ? `${aw.rang_annuel}e` : `${aw.rang_annuel}`) + (classSize ? ` / ${classSize}` : '')
    : '-';
  // ✅ Distinctions / sanctions : cases à cocher
  const cb = (v: any) => (v === 'OUI' || v === 'YES') ? '☒' : '☐';

  // ✅ Rappel : séquentiel → séquence courante + précédentes uniquement
  let recapLabels: string[] = [];
  let recapValues: string[] = [];
  if (bType === 'sequence' && bSeqId) {
    const curIdx = seqList.findIndex((s: any) => s.id === bSeqId);
    const n = curIdx >= 0 ? curIdx + 1 : seqList.length;
    recapLabels = Array.from({ length: n }, (_, i) => L.evalAvg(i + 1));
    recapValues = evals.slice(0, n).map(v => v > 0 ? v.toFixed(2) : '-');
  } else if (bType === 'trimester' && bPeriodId) {
    const curIdx = (bulletin.periods || []).findIndex((p: any) => p.id === bPeriodId);
    const n = curIdx >= 0 ? curIdx + 1 : 3;
    recapLabels = Array.from({ length: n }, (_, i) => L.termAvg(i + 1));
    recapValues = trims.slice(0, n).map(v => v > 0 ? v.toFixed(2) : '-');
  } else {
    recapLabels = [1, 2, 3, 4, 5, 6].map(i => L.evalAvg(i)).concat([1, 2, 3].map(i => L.termAvg(i)));
    recapValues = [...evals, ...trims].map(v => v > 0 ? v.toFixed(2) : '-');
  }

  const footerRows = [
    [L.totalPoints, aw.total_points?.toFixed(2) || '0.00', L.moyPremier, aw.moy_premier ? Number(aw.moy_premier).toFixed(2) : '-', L.absTotal, `${conduct.absence_hours || 0} ${hourWord}`, L.honorRoll, cb(council.tableau_honneur), L.avertWork, cb((council as any).avertissement_travail)],
    [L.totalCoef, aw.total_coef?.toFixed(2) || '0.00', L.moyDernier, aw.moy_dernier ? Number(aw.moy_dernier).toFixed(2) : '-', L.absUnjust, `${conduct.absence_unjustified || 0} ${hourWord}`, L.encouragements, cb(council.encouragements), L.avertConduct, cb((council as any).avertissement_conduite)],
    [`${moyWord} ${periodLabels.moyLabel}`, aw.moy_annuelle?.toFixed(2) || '0.00', L.classAvg, aw.moy_classe?.toFixed(2) || '0.00', L.exclusions, `${conduct.exclusion_days || 0} ${dayWord}`, L.congratulations, cb(council.felicitations), L.blameWork, cb(council.blame_travail)],
    [blang === 'en' ? 'RANK' : 'RANG', rangTxt, '', '', '', '', '', '', L.blameConduct, cb(council.blame_conduite)],
  ];

  const recapColW = (100 / Math.max(recapLabels.length, 1)).toFixed(2);
  const recapHTML = `
  <table style="width:100%;border-collapse:collapse;border:1px solid black;font-size:8px;margin-top:4px;">
    <thead>
      <tr style="background:#f3f4f6;">
        ${recapLabels.map(l => `<th style="border:1px solid black;padding:2px 4px;text-align:center;font-weight:bold;width:${recapColW}%;">${l}</th>`).join('')}
      </tr>
    </thead>
    <tbody>
      <tr>${recapValues.map(v => `<td style="border:1px solid black;padding:2px 4px;text-align:center;font-weight:bold;">${v}</td>`).join('')}</tr>
    </tbody>
  </table>`;

  const footerHTML = footerRows.map((row, idx) => {
    const bg = idx % 2 === 0 ? 'background:white;' : 'background:#f9fafb;';
    return `<tr style="${bg}font-size:8px;">
      <td style="border:1px solid black;padding:2px 4px;width:10%;font-weight:600;">${row[0]}</td>
      <td style="border:1px solid black;padding:2px 4px;width:10%;text-align:right;font-weight:bold;">${row[1]}</td>
      <td style="border:1px solid black;padding:2px 4px;width:10%;font-weight:600;">${row[2]}</td>
      <td style="border:1px solid black;padding:2px 4px;width:10%;text-align:right;font-weight:bold;">${row[3]}</td>
      <td style="border:1px solid black;padding:2px 4px;width:10%;font-weight:600;">${row[4]}</td>
      <td style="border:1px solid black;padding:2px 4px;width:10%;text-align:right;font-weight:bold;">${row[5]}</td>
      <td style="border:1px solid black;padding:2px 4px;width:10%;font-weight:600;">${row[6]}</td>
      <td style="border:1px solid black;padding:2px 4px;width:10%;text-align:center;font-weight:bold;">${row[7]}</td>
      <td style="border:1px solid black;padding:2px 4px;width:10%;font-weight:600;">${row[8]}</td>
      <td style="border:1px solid black;padding:2px 4px;width:10%;text-align:center;font-weight:bold;">${row[9]}</td>
    </tr>`;
  }).join('');

  const footerTableHTML = `
  <table class="footer-table">
    <thead>
      <tr>
        <th colspan="4" style="width:40%;">${workLabel}</th>
        <th colspan="2" style="width:20%;">${conductLabel}</th>
        <th colspan="4" style="width:40%;">${L.council}</th>
      </tr>
      <tr style="background:#f3f4f6;">
        <th colspan="2" style="border:1px solid black;padding:2px 4px;text-align:center;font-weight:bold;">${L.eleve}</th>
        <th colspan="2" style="border:1px solid black;padding:2px 4px;text-align:center;font-weight:bold;">${L.classe}</th>
        <th colspan="2" style="border:1px solid black;padding:2px 4px;"></th>
        <th colspan="2" style="border:1px solid black;padding:2px 4px;text-align:center;font-weight:bold;">${L.distinctions}</th>
        <th colspan="2" style="border:1px solid black;padding:2px 4px;text-align:center;font-weight:bold;">${L.sanctions}</th>
      </tr>
    </thead>
    <tbody>
      ${footerHTML}
    </tbody>
  </table>`;

  // Blocs en-tête république : langue du bulletin à gauche
  const leftBlock = L.repLeft
    ? `<p class="bold">${header.republic_fr}</p>
       <p>${header.motto_fr}</p>
       <p style="margin-top:3px">${header.region}</p>
       <p>${header.department}</p>
       <p>${header.delegation}</p>
       <p>${header.sub_division}</p>`
    : `<p class="bold">${header.republic_en}</p>
       <p>${header.motto_en}</p>
       <p style="margin-top:3px">${header.region?.replace('RÉGION', 'WEST REGION')}</p>
       <p>${header.department?.replace('DÉPARTEMENT', 'DIVISION')}</p>
       <p>${header.delegation?.replace('DÉLÉGATION', 'DELEGATION')}</p>
       <p>${header.sub_division?.replace('ARRONDISSEMENT', 'SUB DIVISION')}</p>`;
  const rightBlock = L.repLeft
    ? `<p class="bold">${header.republic_en}</p>
       <p>${header.motto_en}</p>
       <p style="margin-top:3px">${header.region?.replace('RÉGION', 'WEST REGION')}</p>
       <p>${header.department?.replace('DÉPARTEMENT', 'DIVISION')}</p>
       <p>${header.delegation?.replace('DÉLÉGATION', 'DELEGATION')}</p>
       <p>${header.sub_division?.replace('ARRONDISSEMENT', 'SUB DIVISION')}</p>`
    : `<p class="bold">${header.republic_fr}</p>
       <p>${header.motto_fr}</p>
       <p style="margin-top:3px">${header.region}</p>
       <p>${header.department}</p>
       <p>${header.delegation}</p>
       <p>${header.sub_division}</p>`;

  const bulletinTitle = bType === 'sequence' ? L.titleSequence : bType === 'trimester' ? L.titleTrimester : L.titleAnnual;

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>${blang === 'en' ? 'Report Card' : 'Bulletin'} ${student.matricule}</title>
  <style>
    @page { size: A4 portrait; margin: 10mm; }
    body { font-family: Arial, sans-serif; font-size: 9px; margin: 0; padding: 0; }
    table { border-collapse: collapse; width: 100%; }
    th, td { border: 1px solid black; padding: 2px 4px; }
    .header { text-align: center; border-bottom: 2px solid black; padding-bottom: 8px; margin-bottom: 8px; }
    .header-grid { display: flex; justify-content: space-between; align-items: flex-start; }
    .header-col { flex: 1; }
    .header-col p { margin: 1px 0; font-size: 7px; }
    .header-col .bold { font-weight: bold; font-size: 8px; }
    .logo { width: 50px; height: 50px; display: flex; align-items: center; justify-content: center; margin: 0 8px; }
    .school-name { text-align: center; margin: 4px 0; }
    .school-name h2 { margin: 0; font-size: 12px; color: #1e3a8a; font-weight: bold; }
    .title { text-align: center; border-top: 1px solid #ccc; padding-top: 4px; margin-top: 4px; }
    .title h1 { margin: 0; font-size: 14px; font-weight: bold; text-transform: uppercase; }
    .title p { margin: 2px 0; font-size: 10px; font-weight: 600; }
    .info-table { width: 100%; border: 1px solid black; margin-bottom: 8px; font-size: 8px; }
    .info-table td { border: 1px solid black; padding: 2px 4px; }
    .info-table .label { font-weight: bold; background: #f3f4f6; width: 100px; }
    .subjects-table { width: 100%; border: 1px solid black; font-size: 8px; }
    .subjects-table th { background: #f3f4f6; font-weight: bold; text-align: center; border: 1px solid black; }
    .subjects-table td { border: 1px solid black; padding: 2px 4px; }
    .footer-table { width: 100%; border: 1px solid black; font-size: 8px; margin-top: 4px; }
    .footer-table th { background: #f3f4f6; font-weight: bold; text-align: center; border: 1px solid black; padding: 2px 4px; }
    .footer-table td { border: 1px solid black; padding: 2px 4px; }
    .obs-grid { display: flex; border: 1px solid black; border-top: none; }
    .obs-col { flex: 1; border-right: 1px solid black; padding: 8px; min-height: 80px; font-size: 8px; }
    .obs-col:last-child { border-right: none; }
    .obs-col h4 { margin: -8px -8px 4px -8px; padding: 2px 8px; background: #f3f4f6; font-weight: bold; text-align: center; border-bottom: 1px solid black; }
    .signatures { display: flex; justify-content: space-between; margin-top: 20px; font-size: 8px; }
    .signature-col { text-align: center; width: 30%; }
    .signature-line { border-top: 1px solid #333; margin-top: 30px; padding-top: 2px; }
  </style>
</head>
<body>
  <div class="header">
    <div class="header-grid">
      <div class="header-col" style="text-align:left;">${leftBlock}</div>
      <div class="logo">
        ${header.logo_url ? `<img src="${header.logo_url}" style="width:45px;height:45px;object-fit:contain;" />` : '<div style="width:45px;height:45px;background:#1e3a8a;border-radius:50%;"></div>'}
      </div>
      <div class="header-col" style="text-align:right;">${rightBlock}</div>
    </div>
    <div class="school-name">
      <h2>${header.school_name}</h2>
      <p style="font-size:8px;color:#666;">${header.school_name_en}</p>
      <p style="font-size:7px;color:#999;">${header.address} | ${header.po_box}</p>
    </div>
    <div class="title">
      <h1>${bulletinTitle}</h1>
      <p>${ci.name || student.class_name}, ${ci.level || ''}, ${ci.section || ''}</p>
    </div>
  </div>

  <div style="display:flex;align-items:flex-start;gap:8px;margin-bottom:8px;">
    <div style="width:70px;height:85px;border-radius:6px;overflow:hidden;border:1px solid #ccc;flex-shrink:0;background:#f3f4f6;">
      ${student.photo_url ? `<img src="${resolvePhotoUrl(student.photo_url) || ''}" alt="${student.full_name}" style="width:100%;height:100%;object-fit:cover;" onerror="this.style.display='none'" />` : `<div style="width:100%;height:100%;display:flex;align-items:center;justify-content:center;font-size:10px;font-weight:bold;color:#1e3a8a;">${(student.first_name?.[0] || '') + (student.last_name?.[0] || '')}</div>`}
    </div>
    <table class="info-table" style="flex:1;">
      <tr>
        <td class="label">${L.names}</td>
        <td colspan="3" style="font-weight:bold;text-transform:uppercase;">${student.full_name}</td>
      </tr>
      <tr>
        <td class="label">${L.dob}</td>
        <td>${formatDate(student.date_of_birth)}</td>
        <td class="label">${L.pob}</td>
        <td>${student.place_of_birth || '-'}</td>
      </tr>
      <tr>
        <td class="label">${L.classLabel}</td>
        <td>${student.class_name}</td>
        <td class="label">${L.matricule}</td>
        <td>${student.matricule}</td>
      </tr>
      <tr>
        <td class="label">${L.classTeacher}</td>
        <td>${teacherName}</td>
        <td class="label">${L.year}</td>
        <td>${bulletin.academic_year_id || '2025-2026'}</td>
      </tr>
      <tr>
        <td class="label">${L.gender}</td>
        <td>${student.gender === 'F' ? L.female : student.gender === 'M' ? L.male : '-'}</td>
        <td class="label">${L.situation}</td>
        <td>${isRepeating ? L.repeater : L.newStudent}</td>
      </tr>
      <tr>
        <td class="label">${blang === 'en' ? 'REPEATER' : 'REDOUBLANT'}</td>
        <td colspan="3">${isRepeating ? L.yes : L.no}</td>
      </tr>
    </table>
  </div>

  <table class="subjects-table">
    <thead>
      <tr>
        <th rowspan="2" style="text-align:left;">${L.subjects}</th>
        ${seqHeadersHTML}
        <th colspan="4" style="text-align:center;">${periodLabels.mainTitle}</th>
        ${acquisitionHeaderHTML}
        <th colspan="3" style="text-align:center;">${L.classScores}</th>
        <th rowspan="2" style="text-align:center;">${L.teacherSig.replace('\\n', '<br/>')}</th>
      </tr>
      <tr>
        <th style="text-align:center;">${L.note}</th>
        <th style="text-align:center;">${L.coef}</th>
        <th style="text-align:center;">${L.nxC}</th>
        <th style="text-align:center;">${L.rank}</th>
        ${acquisitionSubHeaderHTML}
        <th style="text-align:center;">${L.min}</th>
        <th style="text-align:center;">${L.moy}</th>
        <th style="text-align:center;">${L.max}</th>
      </tr>
    </thead>
    <tbody>
      ${subjectsRows}
    </tbody>
  </table>

  ${recapHTML}
  ${footerTableHTML}

  <div class="obs-grid">
    <div class="obs-col">
      <h4>${L.obsTitle}</h4>
      ${bulletin.weak_subjects && bulletin.weak_subjects.length > 0 ? `<p style="font-size:8px;font-weight:bold;color:#b91c1c;margin-top:4px;">${L.efforts} ${bulletin.weak_subjects.map((w: any) => `${w.subject_name} (${w.note.toFixed(2)})`).join(', ')}</p>` : ''}
      <p style="font-style:italic;color:#666;margin-top:4px;white-space:pre-wrap;">${council.observations || ''}</p>
    </div>
    <div class="obs-col">
      <h4>${L.visa}</h4>
      <div class="signature-line" style="margin-top:40px;width:60%;margin-left:auto;margin-right:auto;"></div>
    </div>
  </div>

  <div class="signatures">
    <div class="signature-col">
      <p style="font-weight:bold;">${L.parentsSig}</p>
      <div class="signature-line"></div>
    </div>
    <div class="signature-col">
      <p style="font-weight:bold;">${L.principal}</p>
      <div class="signature-line"></div>
    </div>
  </div>
</body>
</html>`;
}

// ═══════════════════════════════════════════════════════════════════════════
// GÉNÉRATEUR OFFICIEL — bulletins TRIMESTRIEL et ANNUEL du 1er cycle
// (approche par compétences, francophone ET anglophone)
// ═══════════════════════════════════════════════════════════════════════════
function generateBulletinOfficialHTML(
  bulletin: BulletinData,
  header: SchoolHeader,
  _lang: string,
  compDescMap?: Map<number, string>,
  classStats?: { min: number; max: number; nbMoyennes: number; tauxReussite: number } | null,
  parentContacts?: Map<number, string>,
): string {
  const student = bulletin.student;
  const bType = bulletin.bulletin_type || 'annual';
  const isComp = !!bulletin.is_competence_approach;
  const conduct = bulletin.conduct || { absence_hours: 0, absence_unjustified: 0, detention_days: 0, exclusion_days: 0, note: 20 };
  const council = bulletin.council || { tableau_honneur: 'NON', encouragements: 'NON', felicitations: 'NON', blame_travail: 'NON', blame_conduite: 'NON', observations: '' } as any;
  const aw = bulletin.annual_work || { total_points: 0, total_coef: 0, moy_annuelle: 0, rang_annuel: 0, moy_classe: 0 } as any;
  const summary = bulletin.summary || { total_points: 0, total_coefficient: 0, general_average: 0, rank: 0 };
  const blang = getBulletinLang(bulletin, _lang);
  const L = getBulletinLabels(blang);
  const isRepeating = !!student.is_repeating;
  const teacherName = bulletin.teacher_name || '';
  const classSize = aw.class_size || 0;

  // Numéro du trimestre courant (pour ne lister que ses compétences)
  let currentPeriodNum: number | null = null;
  if (bType === 'trimester' && bulletin.evaluation_period_id) {
    const period = (bulletin.periods || []).find((p: any) => p.id === bulletin.evaluation_period_id);
    if (period) currentPeriodNum = parseInt((period.code || '').replace(/[^0-9]/g, '')) || null;
  }

  const totalPts = aw.total_points || summary.total_points || 0;
  const totalCoef = aw.total_coef || summary.total_coefficient || 0;
  const moyGen = aw.moy_annuelle || summary.general_average || 0;
  const nb = (v: any) => (v !== undefined && v !== null && Number(v) > 0) ? String(v) : '-';
  const f2 = (v: any) => (v !== undefined && v !== null && Number(v) > 0) ? Number(v).toFixed(2) : '-';

  // Moyenne de la matière, vérifiée : annual_average (annuel) → display_note →
  // moyenne des notes trimestrielles disponibles. 0 = aucune note → exclue.
  const subjectMoy = (subj: BulletinSubject): number => {
    if ((subj.annual_average || 0) > 0) return subj.annual_average;
    if ((subj.display_note || 0) > 0) return subj.display_note;
    if ((subj.grades_count || 0) === 0) return 0;
    const ts = (subj.trimester_scores || []).filter(v => v > 0);
    return ts.length > 0 ? ts.reduce((a, b) => a + b, 0) / ts.length : 0;
  };

  // Libellé réel d'une compétence : si l'API renvoie un nom auto-généré
  // ("FRANCAIS Trimestre 3 Competence 1"), on préfère la vraie description.
  const compDesc = (comp: any): string => {
    // 1) Vraie description chargée depuis /competencies (champ "description")
    if (compDescMap && comp?.id != null && compDescMap.has(Number(comp.id))) {
      return compDescMap.get(Number(comp.id))!;
    }
    // 2) Champs de l'objet bulletin eux-mêmes
    const direct = (comp?.description || comp?.competence_description || '') as string;
    if (direct) return direct;
    // 3) Libellé : si c'est un nom auto-généré "XXX Trimestre N Competence M", le signaler
    const raw = (comp?.label_fr || comp?.label || '') as string;
    if (/trimestre\s+\d+\s+competence/i.test(raw)) {
      return comp?.label_en || raw;
    }
    return raw;
  };

  // Niveau d'acquisition d'une moyenne de compétences
  const getCompLevel = (score: number): string => {
    if (score >= 16) return 'CTBA';
    if (score >= 14) return 'CBA';
    if (score >= 12) return 'CA';
    if (score >= 10) return 'CMA';
    return 'CNA';
  };

  // ── Lignes du tableau des matières ──
  let rows = '';
  const useCompLayout = isComp && bType === 'trimester';
  bulletin.subjects.forEach((subj, idx) => {
    const teacher = subj.teacher_name || '';
    const teacherCell = `${subj.subject_name}<br/><span style="font-size:7.5px;color:#444;">M/Mme ${teacher || '………………'}</span>`;
    const coef = subj.coefficient || 0;
    const subjMoy = subjectMoy(subj);
    const nxCoef = subjMoy * coef;   // moyenne × coefficient (calculé ici)
    const hasGrade = (subj.grades_count || 0) > 0;
    const hasNote = subjMoy > 0;
    const level = getCompLevel(subjMoy);
    const minMaxTxt = (subj.class_stats?.min > 0 || subj.class_stats?.max > 0)
      ? `[${f2(subj.class_stats?.min)} – ${f2(subj.class_stats?.max)}]` : '[ – ]';
    const bg = idx % 2 === 0 ? '' : 'background:#f9fafb;';

    let comps = useCompLayout && subj.competencies
      ? subj.competencies.filter(c => currentPeriodNum === null || c.trimester === currentPeriodNum)
      : [];
    // Si le filtre du trimestre a tout éliminé, afficher toutes les compétences
    if (useCompLayout && comps.length === 0 && subj.competencies && subj.competencies.length > 0) {
      comps = subj.competencies;
    }

    if (comps.length > 0) {
      // ═══ Layout 1er cycle trimestre : COMPÉTENCES ÉVALUÉES (descriptions) ═══
      const rowspan = comps.length;
      comps.forEach((comp, ci) => {
        rows += `<tr style="${bg}page-break-inside:avoid;">
          ${ci === 0 ? `<td rowspan="${rowspan}" style="border:1px solid #000;padding:2px 4px;font-weight:600;font-size:9px;vertical-align:top;">${teacherCell}</td>` : ''}
          <td style="border:1px solid #000;padding:2px 4px;font-size:8px;line-height:1.3;">${compDesc(comp)}</td>
          <td style="border:1px solid #000;padding:2px 4px;text-align:center;font-weight:bold;">${comp.score != null ? Number(comp.score).toFixed(2) : ''}</td>
          ${ci === 0 ? `
          <td rowspan="${rowspan}" style="border:1px solid #000;padding:2px 4px;text-align:center;font-weight:bold;">${hasNote ? subjMoy.toFixed(2) : (hasGrade ? '0.00' : '')}</td>
          <td rowspan="${rowspan}" style="border:1px solid #000;padding:2px 4px;text-align:center;">${coef || ''}</td>
          <td rowspan="${rowspan}" style="border:1px solid #000;padding:2px 4px;text-align:center;font-weight:bold;">${hasNote ? nxCoef.toFixed(2) : ''}</td>
          <td rowspan="${rowspan}" style="border:1px solid #000;padding:2px 4px;text-align:center;font-weight:bold;">${hasNote ? getCote(subjMoy) : ''}</td>
          <td rowspan="${rowspan}" style="border:1px solid #000;padding:2px 4px;text-align:center;font-size:7px;">${minMaxTxt}</td>
          <td rowspan="${rowspan}" style="border:1px solid #000;padding:2px 4px;font-size:8px;font-weight:bold;text-align:center;">${hasNote ? level : ''}<div style="margin-top:12px;"></div></td>` : ''}
        </tr>`;
      });
    } else if (useCompLayout) {
      // ═══ Classe à compétences mais matière sans compétence : 9 cellules (pas de décalage) ═══
      const hasNote2 = subjMoy > 0;
      rows += `<tr style="${bg}page-break-inside:avoid;">
        <td style="border:1px solid #000;padding:2px 4px;font-weight:600;font-size:9px;">${teacherCell}</td>
        <td style="border:1px solid #000;padding:2px 4px;font-size:8px;color:#555;">—</td>
        <td style="border:1px solid #000;padding:2px 4px;text-align:center;font-weight:bold;">${hasNote2 ? subjMoy.toFixed(2) : ((subj.grades_count || 0) > 0 ? '0.00' : '')}</td>
        <td style="border:1px solid #000;padding:2px 4px;text-align:center;font-weight:bold;">${hasNote2 ? subjMoy.toFixed(2) : ((subj.grades_count || 0) > 0 ? '0.00' : '')}</td>
        <td style="border:1px solid #000;padding:2px 4px;text-align:center;">${subj.coefficient ?? ''}</td>
        <td style="border:1px solid #000;padding:2px 4px;text-align:center;font-weight:bold;">${hasNote2 ? nxCoef.toFixed(2) : ''}</td>
        <td style="border:1px solid #000;padding:2px 4px;text-align:center;font-weight:bold;">${hasNote2 ? getCote(subjMoy) : ''}</td>
        <td style="border:1px solid #000;padding:2px 4px;text-align:center;font-size:7px;">${minMaxTxt}</td>
        <td style="border:1px solid #000;padding:2px 4px;font-size:8px;font-weight:bold;text-align:center;">${hasNote2 ? level : ''}<div style="margin-top:8px;"></div></td>
      </tr>`;
    } else {
      // ═══ Layout TRIM 1 / TRIM 2 / TRIM 3 (annuel, ou trimestre sans compétences) ═══
      const t1 = subj.trimester_scores?.[0] ?? 0;
      const t2 = subj.trimester_scores?.[1] ?? 0;
      const t3 = subj.trimester_scores?.[2] ?? 0;
      rows += `<tr style="${bg}page-break-inside:avoid;">
        <td style="border:1px solid #000;padding:2px 4px;font-weight:600;font-size:9px;">${teacherCell}</td>
        <td style="border:1px solid #000;padding:2px 4px;text-align:center;">${t1 > 0 ? t1.toFixed(2) : ''}</td>
        <td style="border:1px solid #000;padding:2px 4px;text-align:center;">${t2 > 0 ? t2.toFixed(2) : ''}</td>
        <td style="border:1px solid #000;padding:2px 4px;text-align:center;">${t3 > 0 ? t3.toFixed(2) : ''}</td>
        <td style="border:1px solid #000;padding:2px 4px;text-align:center;font-weight:bold;">${hasNote ? (subj.display_note || 0).toFixed(2) : (subj.display_note === 0 ? '0.00' : '')}</td>
        <td style="border:1px solid #000;padding:2px 4px;text-align:center;">${subj.coefficient ?? ''}</td>
        <td style="border:1px solid #000;padding:2px 4px;text-align:center;font-weight:bold;">${hasNote ? nxCoef.toFixed(2) : ''}</td>
        <td style="border:1px solid #000;padding:2px 4px;text-align:center;font-weight:bold;">${hasNote ? getCote(subjMoy) : ''}</td>
        <td style="border:1px solid #000;padding:2px 4px;text-align:center;font-size:7px;">${minMaxTxt}</td>
        <td style="border:1px solid #000;padding:2px 4px;font-size:8px;font-weight:bold;text-align:center;">${hasNote ? level : ''}<div style="margin-top:8px;"></div></td>
      </tr>`;
    }
  });

  // ── En-têtes du tableau ──
  const headRow1 = useCompLayout
    ? `<th rowspan="2" style="border:1px solid #000;padding:2px 4px;text-align:center;font-size:8px;">MATIÈRE ET<br/>NOM DE<br/>L'ENSEIGNANT</th>
       <th rowspan="2" style="border:1px solid #000;padding:2px 4px;text-align:center;font-size:8px;">COMPÉTENCES<br/>ÉVALUÉES</th>
       <th style="border:1px solid #000;padding:2px 4px;text-align:center;">N/20</th>
       <th style="border:1px solid #000;padding:2px 4px;text-align:center;">M/20</th>
       <th rowspan="2" style="border:1px solid #000;padding:2px 4px;text-align:center;">Coef</th>
       <th rowspan="2" style="border:1px solid #000;padding:2px 4px;text-align:center;">M x<br/>coef</th>
       <th rowspan="2" style="border:1px solid #000;padding:2px 4px;text-align:center;">COTE</th>
       <th rowspan="2" style="border:1px solid #000;padding:2px 4px;text-align:center;">[Min – Max]</th>
       <th rowspan="2" style="border:1px solid #000;padding:2px 4px;text-align:center;font-size:8px;">Appréciations<br/>et Visa de<br/>l'enseignant</th>`
    : `<th rowspan="2" style="border:1px solid #000;padding:2px 4px;text-align:center;font-size:8px;">MATIÈRE ET<br/>NOM DE<br/>L'ENSEIGNANT</th>
       <th style="border:1px solid #000;padding:2px 4px;text-align:center;">TRIM<br/>1</th>
       <th style="border:1px solid #000;padding:2px 4px;text-align:center;">TRIM<br/>2</th>
       <th style="border:1px solid #000;padding:2px 4px;text-align:center;">TRIM<br/>3</th>
       <th style="border:1px solid #000;padding:2px 4px;text-align:center;">MOY</th>
       <th rowspan="2" style="border:1px solid #000;padding:2px 4px;text-align:center;">Coef</th>
       <th rowspan="2" style="border:1px solid #000;padding:2px 4px;text-align:center;">Moy x<br/>coef</th>
       <th rowspan="2" style="border:1px solid #000;padding:2px 4px;text-align:center;">COTE</th>
       <th rowspan="2" style="border:1px solid #000;padding:2px 4px;text-align:center;">[Min – Max]</th>
       <th rowspan="2" style="border:1px solid #000;padding:2px 4px;text-align:center;font-size:8px;">Appréciations<br/>et Visa de<br/>l'enseignant</th>`;

  // ✅ Moyenne VÉRIFIÉE, calculée dans le bulletin :
  //  - une matière SANS note saisie (grades_count = 0) est EXCLUE : ni son
  //    M×coef ni son coefficient ne comptent ;
  //  - une note de 0/20 SAISIE compte normalement (grades_count > 0).
  let sumCoef = 0, sumNxCoef = 0;
  bulletin.subjects.forEach(subj => {
    const coef = subj.coefficient || 0;
    const hasGrade = (subj.grades_count || 0) > 0;   // note réellement saisie
    if (hasGrade && coef > 0) {
      sumCoef += coef;
      sumNxCoef += subjectMoy(subj) * coef;
    }
  });
  const moyCalc = sumCoef > 0 ? sumNxCoef / sumCoef : 0;
  const moyEff = moyCalc > 0 ? moyCalc : moyGen;  // moyenne vérifiée (sinon backend)
  const totalRow = useCompLayout
    ? `<tr style="background:#e5e7eb;font-weight:bold;">
        <td colspan="2" style="border:1px solid #000;padding:2px 4px;text-align:center;font-size:8px;">TOTAL</td>
        <td colspan="2" style="border:1px solid #000;background:#374151;"></td>
        <td style="border:1px solid #000;padding:2px 4px;text-align:center;">${sumCoef}</td>
        <td style="border:1px solid #000;padding:2px 4px;text-align:center;">${sumNxCoef > 0 ? sumNxCoef.toFixed(2) : ''}</td>
        <td colspan="3" style="border:1px solid #000;padding:2px 4px;text-align:center;font-size:8px;">MOYENNE&nbsp;:&nbsp;&nbsp;${moyEff > 0 ? moyEff.toFixed(2) : ''}</td>
      </tr>`
    : `<tr style="background:#e5e7eb;font-weight:bold;">
        <td style="border:1px solid #000;padding:2px 4px;text-align:center;font-size:8px;">TOTAL</td>
        <td colspan="3" style="border:1px solid #000;background:#374151;"></td>
        <td style="border:1px solid #000;background:#374151;"></td>
        <td style="border:1px solid #000;padding:2px 4px;text-align:center;">${sumCoef}</td>
        <td style="border:1px solid #000;padding:2px 4px;text-align:center;">${sumNxCoef > 0 ? sumNxCoef.toFixed(2) : ''}</td>
        <td colspan="3" style="border:1px solid #000;padding:2px 4px;text-align:center;font-size:8px;">MOYENNE&nbsp;:&nbsp;&nbsp;${moyEff > 0 ? moyEff.toFixed(2) : ''}</td>
      </tr>`;

  // ── Bloc Discipline | Travail de l'élève (avec APPRÉCIATIONS) | Profil de la classe ──
  const titleTxt = bType === 'trimester'
    ? `BULLETIN SCOLAIRE DU ${['PREMIER', 'DEUXIÈME', 'TROISIÈME'][(currentPeriodNum ?? 3) - 1] || '……'} TRIMESTRE`
    : 'BULLETIN SCOLAIRE ANNUEL';
  const absJust = Math.max(0, (conduct.absence_hours || 0) - (conduct.absence_unjustified || 0));
  const moyPrem = aw.moy_premier ? Number(aw.moy_premier).toFixed(2) : '-';
  const moyDern = aw.moy_dernier ? Number(aw.moy_dernier).toFixed(2) : '-';

  // APPRÉCIATIONS : les 5 niveaux empilés, UN SEUL coché selon la moyenne générale
  const genLevel = getCompLevel(moyEff);
  const apprecCell = `
    <td colspan="2" rowspan="4" style="border:1px solid #000;padding:3px 6px;font-size:8px;vertical-align:top;">
      <div style="text-align:center;font-weight:bold;font-size:7px;margin-bottom:2px;">APPRÉCIATIONS</div>
      <div style="margin:1px 0;">CTBA&nbsp;&nbsp;${genLevel === 'CTBA' ? '☒' : '☐'}</div>
      <div style="margin:1px 0;">CBA&nbsp;&nbsp;&nbsp;&nbsp;${genLevel === 'CBA' ? '☒' : '☐'}</div>
      <div style="margin:1px 0;">CA&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;${genLevel === 'CA' ? '☒' : '☐'}</div>
      <div style="margin:1px 0;">CMA&nbsp;&nbsp;&nbsp;&nbsp;${genLevel === 'CMA' ? '☒' : '☐'}</div>
      <div style="margin:1px 0;">CNA&nbsp;&nbsp;&nbsp;&nbsp;${genLevel === 'CNA' ? '☒' : '☐'}</div>
    </td>`;

  // Profil de la classe : UNIQUEMENT ces 4 éléments (2 cellules par ligne, 4 lignes)
  // Profil de la classe : valeurs calculées sur tous les élèves (page) en priorité
  const profileRows: string[] = [
    `<td style="border:1px solid #000;padding:2px 4px;font-size:8px;">Moyenne. Générale.</td>
     <td style="border:1px solid #000;padding:2px 4px;text-align:center;font-weight:bold;">${f2(aw.moy_classe)}</td>`,
    `<td style="border:1px solid #000;padding:2px 4px;font-size:8px;">[Min – Max]</td>
     <td style="border:1px solid #000;padding:2px 4px;text-align:center;font-weight:bold;">${
       classStats ? classStats.min.toFixed(2) + ' – ' + classStats.max.toFixed(2) : (moyPrem + ' – ' + moyDern)
     }</td>`,
    `<td style="border:1px solid #000;padding:2px 4px;font-size:8px;">Nombre de moyennes</td>
     <td style="border:1px solid #000;padding:2px 4px;text-align:center;font-weight:bold;">${classStats ? classStats.nbMoyennes : '-'}</td>`,
    `<td style="border:1px solid #000;padding:2px 4px;font-size:8px;">Taux de réussite</td>
     <td style="border:1px solid #000;padding:2px 4px;text-align:center;font-weight:bold;">${classStats ? classStats.tauxReussite + '%' : '-'}</td>`,
  ];

  // Chaque ligne = 10 cellules : 4 (discipline) + 2 (travail) + 2 (appréciations, ligne 1) + 2 (profil)
  const disc = (l1: string, v1: string, l2: string, v2: string) =>
    `<td style="border:1px solid #000;padding:2px 4px;font-size:8px;">${l1}</td>
     <td style="border:1px solid #000;padding:2px 4px;text-align:center;">${v1}</td>
     <td style="border:1px solid #000;padding:2px 4px;font-size:8px;">${l2}</td>
     <td style="border:1px solid #000;padding:2px 4px;text-align:center;">${v2}</td>`;
  const work = (lbl: string, val: string) =>
    `<td style="border:1px solid #000;padding:2px 4px;font-size:8px;">${lbl}</td>
     <td style="border:1px solid #000;padding:2px 4px;text-align:center;font-weight:bold;">${val}</td>`;

  let middleRows = '';
  if (bType === 'trimester') {
    middleRows = `
      <tr>
        ${disc('Abs. non J. (h)', nb(conduct.absence_unjustified), 'Avertissement de conduite', (council as any).avertissement_conduite === 'OUI' ? '☒' : '☐')}
        ${work('TOTAL GENERAL', totalPts > 0 ? totalPts.toFixed(2) : '-')}
        ${apprecCell}
        ${profileRows[0]}
      </tr>
      <tr>
        ${disc('Abs just. (h)', absJust > 0 ? String(absJust) : '-', 'Blâme de conduite', council.blame_conduite === 'OUI' ? '☒' : '☐')}
        ${work('COEF', totalCoef ? String(totalCoef) : '-')}
        ${profileRows[1]}
      </tr>
      <tr>
        ${disc('Retards (nombre de fois)', '-', 'Exclusions (jours)', nb(conduct.exclusion_days))}
        ${work('MOYENNE TRIM', moyEff > 0 ? moyEff.toFixed(2) : '-')}
        ${profileRows[2]}
      </tr>
      <tr>
        ${disc('Consignes (heures)', nb(conduct.detention_days), 'Exclusion définitive', (council as any).exclusion_definitive === 'OUI' ? '☒' : '☐')}
        ${work('COTE', moyEff > 0 ? getCote(moyEff) : '-')}
        ${profileRows[3]}
      </tr>`;
  } else {
    const decision = (((bulletin.student as any).decision || '') as string);
    const dl = decision.toLowerCase();
    const decisionCell = `
    <td colspan="2" rowspan="4" style="border:1px solid #000;padding:3px 6px;font-size:8px;vertical-align:top;">
      <div style="text-align:center;font-weight:bold;font-size:7px;margin-bottom:2px;">DÉCISION DU CONSEIL DE CLASSE</div>
      <div style="margin:1px 0;">Promu(e)&nbsp;&nbsp;${dl.includes('promu') || dl.includes('admi') ? '☒' : '☐'}</div>
      <div style="margin:1px 0;">Redouble&nbsp;&nbsp;${dl.includes('redoubl') ? '☒' : '☐'}</div>
      <div style="margin:1px 0;">Exclu(e) pour : ${dl.includes('exclu') ? decision : ''}</div>
    </td>`;
    middleRows = `
      <tr>
        ${disc('Abs. non J. (h)', nb(conduct.absence_unjustified), 'Avertissement de conduite', (council as any).avertissement_conduite === 'OUI' ? '☒' : '☐')}
        ${work('TOTAL GENERAL', totalPts > 0 ? totalPts.toFixed(2) : '-')}
        ${decisionCell}
        ${profileRows[0]}
      </tr>
      <tr>
        ${disc('Abs just. (h)', absJust > 0 ? String(absJust) : '-', 'Blâme de conduite', council.blame_conduite === 'OUI' ? '☒' : '☐')}
        ${work('COEF.', totalCoef ? String(totalCoef) : '-')}
        ${profileRows[1]}
      </tr>
      <tr>
        ${disc('Retards (nombre de fois)', '-', 'Exclusions (jours)', nb(conduct.exclusion_days))}
        ${work('MOYENNE ANNUELLE', moyEff > 0 ? moyEff.toFixed(2) : '-')}
        ${profileRows[2]}
      </tr>
      <tr>
        ${disc('Consignes (h)', nb(conduct.detention_days), 'Exclusion définitive', (council as any).exclusion_definitive === 'OUI' ? '☒' : '☐')}
        ${work('COTE', moyEff > 0 ? getCote(moyEff) : '-')}
        ${profileRows[3]}
      </tr>`;
  }

  // Lignes d'en-tête avec étoiles (*******************) à droite de chaque ligne,
  // style officiel. Les délégations sont dérivées de la région/département configurés.
  const STARS = '*******************';
  // Étoiles SOUS chaque ligne de texte (style officiel)
  const starLine = (txt: string, bold = false) =>
    `<p style="margin:2px 0 0;font-size:6.5px;white-space:nowrap;overflow:hidden;">
       <span${bold ? ' style="font-weight:bold;font-size:7.5px;"' : ''}>${txt}</span>
     </p>
     <p style="margin:0 0 1px;font-size:5.5px;letter-spacing:1px;line-height:1;white-space:nowrap;overflow:hidden;">${STARS}</p>`;
  const delegRegionFr = (header.region || 'RÉGION DE …').replace('RÉGION', 'DELEGATION REGIONALE');
  const delegDeptFr = (header.department || 'DÉPARTEMENT DE …').replace('DÉPARTEMENT', 'DELEGATION DEPARTEMENTALE');
  // Extraction propre : "RÉGION DE L'OUEST" → OUEST → WEST ; "DÉPARTEMENT DE LA MIFI" → MIFI
  const stripAcc = (v: string) => (v || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  const REGION_MAP: Record<string, string> = {
    'OUEST': 'WEST', 'NORD': 'NORTH', 'SUD': 'SOUTH', 'EST': 'EAST',
    'EXTREME NORD': 'FAR NORTH', 'ADAMAOUA': 'ADAMAWA',
    'NORD OUEST': 'NORTH WEST', 'SUD OUEST': 'SOUTH WEST', 'CENTRE': 'CENTRE', 'LITTORAL': 'LITTORAL',
  };
  const regionRaw = stripAcc(header.region || '').replace(/^\s*REGION\s+(DE\s+)?(L'|LA\s+|LE\s+)?/i, '').trim();
  const delegRegionEn = REGION_MAP[regionRaw.toUpperCase()] || regionRaw || '…';
  const deptRaw = stripAcc(header.department || '').replace(/^\s*DEPARTEMENT\s+(DE\s+)?(LA\s+|L'|LE\s+)?/i, '').trim();
  const delegDeptEn = deptRaw || '…';
  const linesFr = [
    [header.republic_fr, true],
    [header.motto_fr, false],
    ['MINISTERE DES ENSEIGNEMENTS SECONDAIRES', false],
    [delegRegionFr, false],
    [delegDeptFr, false],
  ];
  const linesEn = [
    [header.republic_en, true],
    [header.motto_en, false],
    ['MINISTRY OF SECONDARY EDUCATION', false],
    [delegRegionEn + ' REGIONAL DELEGATION', false],
    [delegDeptEn + ' DIVISIONAL DELEGATION', false],
  ];
  const renderLines = (lines: any[]) => lines.map(([t, b]) => starLine(t, b)).join('');
  const leftBlock = L.repLeft ? renderLines(linesFr) : renderLines(linesEn);
  const rightBlock = L.repLeft ? renderLines(linesEn) : renderLines(linesFr);

  return `<!DOCTYPE html>
<html>
<head>
<meta charset="UTF-8">
<title>${titleTxt} — ${student.matricule}</title>
<style>
  @page { size: A4 portrait; margin: 8mm; }
  * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  body { font-family: Arial, sans-serif; font-size: 8px; margin: 0; padding: 0; overflow-x: hidden; }
  table { border-collapse: collapse; width: 100%; }
  .hdr { text-align: center; border-bottom: 1.5px solid #000; padding-bottom: 6px; margin-bottom: 6px; }
  .hdr-grid { display: flex; justify-content: space-between; align-items: flex-start; }
  .hdr-col { flex: 1; }
  .hdr-col p { margin: 1px 0; font-size: 6.5px; }
  .hdr-col .bold { font-weight: bold; font-size: 7.5px; }
  .logo { width: 60px; text-align: center; font-size: 6.5px; color: #555; }
  .logo-circle { width: 48px; height: 48px; border: 1px dashed #999; border-radius: 50%; margin: 0 auto; display: flex; align-items: center; justify-content: center; font-size: 6px; text-align: center; }
  .title h1 { margin: 2px 0 0; font-size: 13px; font-weight: bold; text-transform: uppercase; }
  .title p { margin: 1px 0; font-size: 9px; font-weight: 600; }
  .info td { border: 1px solid #000; padding: 2px 4px; font-size: 7.5px; }
  .info .lbl { font-weight: bold; }
  .photo-box { width: 70px; height: 85px; border: 1px solid #000; display: flex; align-items: center; justify-content: center; font-size: 7px; color: #555; flex-shrink: 0; overflow: hidden; }
  .grid th, .grid td { border: 1px solid #000; padding: 3px 4px; font-size: 8.5px; overflow-wrap: break-word; word-break: break-word; line-height: 1.25; }
  .grid th { background: #f3f4f6; font-weight: bold; text-align: center; }
  .sign td { border: 1px solid #000; padding: 3px 4px; font-size: 8px; font-weight: bold; text-align: center; height: 55px; vertical-align: top; }
</style>
</head>
<body>
  <div class="hdr">
    <table style="width:100%;border:none;margin:0;">
      <tr>
        <td style="width:39%;border:none;vertical-align:top;padding:0;">${leftBlock}</td>
        <td style="width:22%;border:none;vertical-align:top;padding:0;text-align:center;">${
          header.logo_url
            ? `<img src="${header.logo_url}" alt="Logo" style="width:52px;height:52px;object-fit:contain;" />`
            : `<div class="logo-circle">logo et<br/>immatriculation<br/>de l'établissement</div>`
        }</td>
        <td style="width:39%;border:none;vertical-align:top;padding:0;">${rightBlock}</td>
      </tr>
    </table>
    <div style="text-align:center;margin-top:3px;">
      <div style="font-size:13px;font-weight:bold;color:#1e3a8a;">${header.school_name}</div>
      <div style="font-size:8px;font-weight:600;color:#1e3a8a;">${header.school_name_en}</div>
      <div style="font-size:6.5px;color:#555;">${header.address} | ${header.po_box}</div>
    </div>
    <div class="title">
      <h1>${titleTxt}</h1>
      <p>Année scolaire : ${bulletin.academic_year_id || '……'}</p>
    </div>
  </div>

  <div style="display:flex;gap:6px;margin-bottom:6px;align-items:stretch;">
    <div class="photo-box">Photo de l'élève</div>
    <table class="info" style="flex:1;">
      <tr>
        <td class="lbl" colspan="2">Nom et Prénoms de l'élève : <b>${student.full_name}</b></td>
        <td class="lbl" style="width:22%;">Classe : <b>${student.class_name}</b></td>
      </tr>
      <tr>
        <td class="lbl" style="width:40%;">Date et lieu de naissance : ${student.date_of_birth ? new Date(student.date_of_birth).toLocaleDateString('fr-FR') : '……'} à ${student.place_of_birth || '……'}</td>
        <td class="lbl" style="width:20%;">Genre : ${student.gender === 'F' ? 'F' : student.gender === 'M' ? 'M' : ''}</td>
        <td class="lbl">Effectif : ${classSize || ''}</td>
      </tr>
      <tr>
        <td class="lbl">Identifiant Unique : ${student.matricule}</td>
        <td class="lbl">Redoublant : Oui ${isRepeating ? '☒' : '☐'}&nbsp;&nbsp;Non ${isRepeating ? '☐' : '☒'}</td>
        <td class="lbl">Professeur principal : ${teacherName}</td>
      </tr>
      <tr>
        <td class="lbl" colspan="3">Noms et contacts des Parents / Tuteurs : ${(parentContacts && parentContacts.get(student.id)) || '………………………………………………………………'}</td>
      </tr>
    </table>
  </div>

  <table class="grid" style="table-layout:fixed;width:100%;max-width:100%;">
    <colgroup>
      ${useCompLayout
        ? `<col style="width:11.5%"><col style="width:26%"><col style="width:7%"><col style="width:7%"><col style="width:5.5%"><col style="width:8%"><col style="width:6%"><col style="width:8.5%"><col style="width:20.5%">`
        : `<col style="width:15%"><col style="width:6%"><col style="width:6%"><col style="width:6%"><col style="width:7%"><col style="width:5.5%"><col style="width:8%"><col style="width:6%"><col style="width:9%"><col style="width:31.5%">`}
    </colgroup>
    <thead>
      <tr>${headRow1}</tr>
    </thead>
    <tbody>
      ${rows}
      ${totalRow}
    </tbody>
  </table>

  <table class="grid" style="margin-top:4px;">
    <thead>
      <tr>
        <th colspan="4" style="width:36%;">Discipline</th>
        <th colspan="4" style="width:36%;">Travail de l'élève</th>
        <th colspan="2" style="width:28%;">Profil de la classe</th>
      </tr>
    </thead>
    <tbody>${middleRows}</tbody>
  </table>

  <table class="grid sign" style="margin-top:0;border-top:none;">
    <tr>
      <td style="width:40%;">Appréciation du travail de l'élève${council.observations ? `<br/><span style="font-weight:normal;font-size:7px;">${council.observations}</span>` : ''}</td>
      <td style="width:20%;">Visa du parent / Tuteur</td>
      <td style="width:20%;">Nom et visa du professeur principal</td>
      <td style="width:20%;">Le Chef d'établissement</td>
    </tr>
  </table>
</body>
</html>`;
}